/**
 * AppRegistration — ONE reusable shared-credential "app registration" surface
 * (CD-BRIEF-shared-app-registration, kit 735eab65, 2026-07-24). Renders ANY
 * provider whose IntegrationDetail arrives with `sharedCredentials: true`,
 * entirely from backend data: shared credential fields → per-feature toggle
 * list (updateIntegrationFeature) → aggregated scopes panel → recent activity.
 * Zero provider-specific layout — the mock serves both `google` and
 * `microsoft` through these same components.
 */
import { createContext, useContext, useEffect, useRef, useState, type CSSProperties } from "react";
import { Btn, Card, ColorPicker, Icon, Input, Pill, T, Toggle, tx } from "@v0y4ge/jrni-ui";
import { getIntegrationDetail, startIntegrationConnect, updateIntegrationConfig, updateIntegrationFeature } from "@v0y4ge/api-client";
import { getGraphLogSettings, updateGraphLogSettings, getMicrosoftStatus, verifyMicrosoftConnection, updateMicrosoftSettings, getMicrosoftSettings, listMicrosoftEntraGroups, runMicrosoftUserSync } from "./ms-graph-api";
import { ProviderLogo } from "./ProviderLogo";
import {
  PASS_COLOR_KEYS, PASS_CONTRAST_MIN, PASS_STYLE_DEFAULTS, isPassHex, passContrastNotice,
  readPassAppearance, refreshConnections, resolvePassColors, scopeMeta,
  type IntegInput, type PassAppearance, type PassColorKey,
} from "./integration-configs";
import { GsuiteDirectoryPanel } from "./GsuiteDirectoryPanel";
import { GoogleServiceAccountForm, CalendarSyncStatusPanel } from "./CalendarSyncPanels";
import { FeatureCredentialUploads } from "./WalletCredentialUpload";
import { putIntegrationFeatureSetting } from "./wallet-appearance-api";
import { refusalMessage, saveConfigDrafts, savesAsFeatureSetting } from "./integration-config-save";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { useT, t as i18nT } from "../../lib/i18n";
import { useTenantPublicConfig } from "../../lib/public-url";
import { friendlyDateShort, friendlyTime } from "../../lib/datetime";

/* Backend shapes (api-contracts integrations.ts) — tolerant reads only. */
export interface RegFeature {
  key: string; name: string; description?: string; enabled: boolean; status?: string;
  /* The stored on/off DECISION (voyage#681). `enabled` is EFFECTIVE — decision AND required
   * credentials present — so a credential-UPLOAD form must gate on THIS, or on
   * status === 'requires_input', and never on `enabled`: the form is the only way to supply the
   * credential that would make `enabled` true, so gating on it deadlocks. Anything that ACTS on the
   * feature still gates on `enabled`. */
  requestedEnabled?: boolean;
  /* Whether this feature's required configFields are all configured. */
  credentialsSatisfied?: boolean;
  /* Human twin of a snake_case machine status ('requires_input'). */
  statusLabel?: string;
  /** Why a switched-on feature is not working yet, set by the server while requires_input (CC 0535). */
  waitingReason?: string;
  scopes?: string[]; adminConsentRequired?: boolean; icon?: string; category?: string; requiredApi?: string; ssoManagedExternally?: boolean;
  /* Feature grouping + per-feature settings (2026-08-04): a registration whose features
   * belong to distinct API products (Twilio: Programmable Messaging vs Conversations)
   * points each feature at a group, and a channel's own settings (its sender) ride on
   * the feature instead of the shared-credential card. */
  group?: string; configFields?: Record<string, unknown>[];
}
export interface RegFeatureGroup {
  key: string; name?: string; description?: string; docs?: string; configFields?: Record<string, unknown>[];
}
/* What a registration can DO — provider auth-model facts, never current state.
 * Read tolerantly: camel as the deployed backend sends, snake as api-contracts'
 * catalog schema declares; only an explicit false is a denial. */
export interface RegCapabilities {
  verify?: boolean; runNow?: boolean; configure?: boolean; oauthFlow?: boolean; apiKeyFlow?: boolean;
  run_now?: boolean; oauth_flow?: boolean; api_key_flow?: boolean;
}
export function regCap(d: RegDetail | null | undefined, camel: keyof RegCapabilities, snake: keyof RegCapabilities): boolean | null {
  const c = d?.capabilities;
  if (!c) return null;
  const v = c[camel] ?? c[snake];
  return typeof v === "boolean" ? v : null;   /* null = not stated → legacy behaviour */
}
export interface RegDetail {
  provider: string; name: string; auth?: string; desc?: string; brand?: string; icon?: string; docs?: string;
  capabilities?: RegCapabilities;
  sharedCredentials?: boolean; features?: RegFeature[]; featureGroups?: RegFeatureGroup[]; callback?: string;
  /* Provider-specific panel copy — a registration that isn't OAuth shouldn't be described
   * in OAuth language (Twilio has account resources and an inbound webhook, not scopes
   * and a redirect URI). Absent = the default copy below. */
  featuresTitle?: string; featuresSubtitle?: string; scopesTitle?: string; scopesHint?: string;
  callbackLabel?: string; callbackHint?: string;
  registrationId?: string; featureKey?: string;
  configFields?: Record<string, unknown>[]; permissions?: string[]; scopes?: string[];
  activity?: { at?: string; e?: string; occurredAt?: string; detail?: string }[];
  status?: string; needsInput?: boolean;
  /* `missingRequiredFields` names the config-field KEYS that keep requiresInput true
   * (CD docket 2026-09-15 item 3) — the diagnostic the "Requires input" badge needs. */
  configuration?: { status?: string; requiresInput?: boolean; missingRequiredFields?: string[] }; updatedAt?: string;
  /* Tolerant read — NOT a contracted field (confirmed absent from api-contracts and
   * workers/integrations on the importer side, 2026-08-12). IntegrationDetail.tsx:127 reads
   * it optionally; undefined falls through to the capability check, which is the
   * pre-existing behaviour. If the upload connection model becomes real, contract it
   * properly and tighten this type. */
  connectionModel?: string;
  /* The backend's own verdict on this registration (CC 0118): `data.integration.status`
   * ("connected" | "needs-attention" | "error" | "disconnected") and `data.lastError`
   * ({ code, message }), both already in the detail response. */
  integration?: { status?: string };
  lastError?: { code?: string; message?: string } | null;
}

/* The banner a registration earns, from the backend's status (CC 0118). Filled-in
 * fields alone never make it "connected": only integration.status "connected" does.
 * regRequiresInput still decides "disconnected" (fields still to enter). */
export function regStatusKind(d: RegDetail): "connected" | "configured-unverified" | "error" | "disconnected" {
  if (regRequiresInput(d)) return "disconnected";
  const s = String(d.integration?.status ?? "").toLowerCase().replace(/[\s_]+/g, "-");
  if (s === "connected") return "connected";
  if (s === "error") return "error";
  if (s === "disconnected" || s === "not-connected") return "disconnected";
  return "configured-unverified";
}

/* A registration is "requires input" (no usable shared credentials) when the
 * backend says so — right after Disconnect, or before the first connect. */
export function regRequiresInput(d: RegDetail): boolean {
  if (d.configuration && typeof d.configuration.requiresInput === "boolean") return d.configuration.requiresInput;
  if (typeof d.needsInput === "boolean") return d.needsInput;
  const s = String(d.status ?? "").toLowerCase();
  return /requires|disconnected|not.?connected/.test(s);
}

/* The config-field keys still empty at registration level, with their human labels
 * resolved from whichever card declares them (registration, group, or feature). */
export function regMissingFields(d: RegDetail): string[] {
  const v = d.configuration?.missingRequiredFields;
  // The retired organizer field is never a prerequisite (CC 0135 §1).
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x.length > 0 && RETIRED_FIELD_KEYS.indexOf(x) < 0) : [];
}
function regFieldLabel(d: RegDetail, key: string): string {
  const pools: Record<string, unknown>[][] = [Array.isArray(d.configFields) ? d.configFields : []];
  (Array.isArray(d.featureGroups) ? d.featureGroups : []).forEach((g) => pools.push(Array.isArray(g.configFields) ? g.configFields : []));
  (Array.isArray(d.features) ? d.features : []).forEach((f) => pools.push(Array.isArray(f.configFields) ? f.configFields : []));
  for (const pool of pools) {
    for (const cf of pool) {
      const k = typeof cf.key === "string" ? cf.key : (typeof cf.id === "string" ? cf.id : "");
      if (k === key && typeof cf.label === "string" && cf.label) return cf.label;
    }
  }
  /* No declared label (the field lives on a card this response didn't send): fall
   * back to a readable form of the key rather than the raw snake_case. */
  return key.replace(/_/g, " ").replace(/\b(sid|api|url|uri|id)\b/gi, (s) => s.toUpperCase()).replace(/^./, (c) => c.toUpperCase());
}
export function regMissingFieldLabels(d: RegDetail): string[] { return regMissingFields(d).map((k) => regFieldLabel(d, k)); }

/* Unwrap a {data} envelope (the client returns either shape across ops). */
export function regFromResponse(r: unknown): RegDetail | null {
  const rec = r && typeof r === "object" && "data" in (r as Record<string, unknown>) ? (r as { data: unknown }).data : r;
  if (!rec || typeof rec !== "object") return null;
  /* Shallow-cloned so the returned record is never reference-identical to the
   * previous one (2026-08-13). Every caller feeds this straight into React state
   * (`onDetail` / `onSaved`), and a data source that mutates its record in place
   * and hands back the SAME object makes setState a no-op: React bails out, and
   * any sibling reading the record — the pass preview reading its feature's
   * configFields — keeps painting the previous values while the component that
   * re-rendered for its own reasons shows the new ones. That is exactly how
   * clearing a pass colour left the Appearance group saying "Platform default"
   * while the card still painted the cleared colour.
   *
   * A shallow clone is sufficient and deliberate: arrays that genuinely changed
   * are REPLACED by the source rather than mutated in place, so children see the
   * new arrays, and the clone supplies the one new top-level identity React
   * needs to re-render at all. */
  return { ...(rec as RegDetail) };
}

const regFeatures = (d: RegDetail): RegFeature[] => (Array.isArray(d.features) ? d.features : []);
/* The Meeting organizer mailbox is retired (CC 0135 §1): a Teams meeting is created in
   the booked staff member's own calendar. Any served organizer field is not drawn. */
const RETIRED_FIELD_KEYS = ["organizer", "meeting_organizer", "organizer_mailbox"];
const notRetired = (f: unknown) => RETIRED_FIELD_KEYS.indexOf(String((f as Record<string, unknown>)?.key ?? (f as Record<string, unknown>)?.id ?? "")) < 0;
const toInput = (f: Record<string, unknown>): IntegInput => {
  const fieldKey = typeof f.key === "string" ? f.key : (typeof f.id === "string" ? f.id : undefined);
  // The contract's config-field `type` now has 'date' (CC 0114), so the old
  // *_expires_at coercion is gone: the backend declares the type.
  const type = (typeof f.type === "string" ? f.type : "text") as IntegInput["type"];
  return {
    l: String(f.l ?? f.label ?? f.key ?? "Field"),
    placeholder: typeof f.placeholder === "string" ? f.placeholder : "",
    type,
    required: !!f.required,
    filled: typeof f.filled === "boolean" ? f.filled : String(f.filled ?? f.value ?? ""),
    readonly: !!f.readonly,
    configured: !!f.configured,
    masked: !!f.masked,
    desc: typeof f.description === "string" ? f.description : (typeof f.desc === "string" ? f.desc : undefined),
    fieldKey,
    // A secret's recorded expiry (CC 0114 §3): shown as "Expires on …" under the field.
    expiresAt: typeof f.expiresAt === "string" && f.expiresAt ? f.expiresAt : undefined,
  };
};

/* Status pill — tone inferred from the backend's display status string. */
function FeatureStatusPill({ status, label }: { status?: string; label?: string }) {
  if (!status) return null;
  const v = status.toLowerCase();
  /* Feature status is a MACHINE value ('requires_input'); render the served human
   * twin when there is one, else humanize rather than print the code. */
  const shown = label || (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(status)
    ? status.replace(/_/g, " ").replace(/^./, (ch) => ch.toUpperCase())
    : status);
  const tone: "success" | "warn" | "danger" | "neutral" =
    /error|fail/.test(v) ? "danger"
      : /needs|pending|awaiting|requires|consent|attention|expir/.test(v) ? "warn"
        : v === "connected" || /verified|healthy|active|live/.test(v) ? "success"
          : "neutral";
  return <Pill tone={tone} dot={tone === "success"}>{shown}</Pill>;
}

/* ─── Microsoft capability layer (built 2026-07-22/23, RESTORED 2026-07-27) ──
 * The Microsoft registration is backed by two provider-specific ops the generic
 * registration contract has no equivalent for: a POST verify and a live
 * per-capability status read (kit `cd-wiring-kit-ms-app-registration-final`).
 * They render as an ADDITIVE layer on the generic surface — every other
 * provider (google…) renders exactly as it did before. */
const MS_PROVIDER = "microsoft";
// ui-config — registration feature key → capability key in getMicrosoftStatus.
const MS_FEATURE_CAPABILITY: Record<string, string> = {
  calendar: "calendar", outlook: "calendar",
  teams: "teams", msteams: "teams",
  entra: "user_sync", user_sync: "user_sync",
  email_send: "email", msmail: "email", email: "email",
};
// ui-config — verify failure copy keyed by the backend error CODE (never HTTP status).
// Keys + byte-exact English as filed in REQ-059.
const MS_VERIFY_ERRORS: Record<string, { k: string; l: string }> = {
  TOKEN_REJECTED: { k: "settings.integrationDetail.verifyTokenRejected", l: "Credentials rejected — check the client ID, client secret and tenant ID." },
  NOT_CONFIGURED: { k: "settings.integrationDetail.verifyNotConfigured", l: "Not configured — enter the app-registration credentials first." },
  RATE_LIMITED: { k: "settings.integrationDetail.verifyRateLimited", l: "Rate limited by Microsoft — try again in a moment." },
  PROVIDER_UNREACHABLE: { k: "settings.integrationDetail.verifyProviderUnreachable", l: "Microsoft couldn't be reached — try again." },
};
// ui-config — Microsoft activity grouping (operator 2026-07-22): rows are read from
// each feature's OWN backend record and grouped under these category titles.
const MS_ACTIVITY_CATEGORIES: { key: string; title: string; members: string[] }[] = [
  /* Only real providers (CC 0114 ask 3): "msgraphlog", "entra" and "msmail" are feature
     keys of the Microsoft registration and answered 400 UNKNOWN_PROVIDER, so their
     groups were always empty. "Users" and "Mails sent" are removed with them. */
  { key: "events", get title() { return i18nT("settings.appRegistration.activity.events", "Events"); }, members: ["outlook"] },
  { key: "teams", get title() { return i18nT("settings.appRegistration.activity.teams", "Teams"); }, members: ["msteams"] },
];

interface MsCapability { enabled?: boolean; state?: string; note?: string | null; error?: { message?: string } | null; last_verified_at?: string | null }
interface MsVerifyResult { ok?: boolean; latency_ms?: number; account?: { label?: string; email?: string } | null; error?: { code?: string; message?: string } | null }

const unwrap = (r: unknown): Record<string, unknown> | null => {
  const rec = r && typeof r === "object" && "data" in (r as Record<string, unknown>) ? (r as { data: unknown }).data : r;
  return rec && typeof rec === "object" ? rec as Record<string, unknown> : null;
};

/* Live per-capability status. Additive: if the op is unavailable the page keeps
 * rendering the backend's own feature status — never a fabricated state. */
function useMicrosoftStatus(provider: string, nonce: number): Record<string, MsCapability> | null {
  const [caps, setCaps] = useState<Record<string, MsCapability> | null>(null);
  useEffect(() => {
    if (provider !== MS_PROVIDER) { setCaps(null); return; }
    let alive = true;
    getMicrosoftStatus({ probe: false })
      .then((r) => { const rec = unwrap(r); if (alive) setCaps((rec?.capabilities as Record<string, MsCapability>) ?? null); })
      .catch(() => { if (alive) setCaps(null); });
    return () => { alive = false; };
  }, [provider, nonce]);
  return caps;
}

/* The whole status record (probe=false): admin_consent, settings (sender). */
function useMicrosoftStatusRecord(provider: string, nonce: number): Record<string, unknown> | null {
  const [rec, setRec] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    if (provider !== MS_PROVIDER) { setRec(null); return; }
    let alive = true;
    getMicrosoftStatus({ probe: false }).then((r) => { if (alive) setRec(unwrap(r)); }).catch(() => { if (alive) setRec(null); });
    return () => { alive = false; };
  }, [provider, nonce]);
  return rec;
}

/* ── Microsoft admin consent (CC 0115) ─────────────────────────────────────
 * startIntegrationConnect('microsoft') answers { url }: Microsoft's admin-consent page
 * for the saved directory and app. Opened in this tab, so Microsoft returns here with
 * ?microsoft=consented or ?microsoft=consent_failed&reason=<code>. */
export async function startMicrosoftConsent(): Promise<void> {
  try {
    const res = await startIntegrationConnect(MS_PROVIDER, { redirect_to: window.location.href }) as { url?: string };
    if (!res || typeof res.url !== "string" || !res.url) throw new Error("No consent address came back.");
    window.location.assign(res.url);
  } catch (e) {
    toast({ tone: "warn", title: "Couldn't start admin consent", subtitle: e instanceof Error ? e.message : String(e) });
  }
}
/** Client ID, Directory (tenant) ID and the secret are all saved. */
export function msCredentialsSaved(d: RegDetail | null | undefined): boolean {
  if (!d || d.provider !== MS_PROVIDER) return false;
  const fields = Array.isArray(d.configFields) ? d.configFields : [];
  const has = (key: string) => fields.some((f) => (f.key === key || f.id === key) && (f.configured === true || (typeof f.filled === "string" && f.filled !== "") || (typeof f.value === "string" && f.value !== "")));
  return has("client_id") && has("entra_tenant_id") && has("client_secret");
}
const CONSENT_REASON: Record<string, string> = {
  consent_not_granted: "Consent wasn't granted.",
  not_configured: "Save the Application (client) ID, Directory (tenant) ID and client secret first.",
  registration_changed: "The registration changed while consent was in progress. Try again.",
  tenant_mismatch: "Consent was given in a different Microsoft directory than the one saved here.",
  access_denied: "The Microsoft admin declined the request.",
};
/** Grant admin consent: the action, when it was granted, and the last error. */
export function MicrosoftAdminConsent({ detail, canWrite }: { detail: RegDetail; canWrite: boolean }) {
  const [consent, setConsent] = useState<{ granted_at?: string | null; last_error?: string | { message?: string } | null } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    getMicrosoftStatus({ probe: false })
      .then((res) => { const rec = unwrap(res); if (alive) setConsent((rec?.admin_consent as typeof consent) ?? null); })
      .catch(() => { if (alive) setConsent(null); });
    return () => { alive = false; };
  }, [detail]);
  /* The return from Microsoft: one toast, then the query is cleaned up. */
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const v = sp.get("microsoft");
    if (!v) return;
    if (v === "consented") toast({ tone: "success", title: "Admin consent granted" });
    else if (v === "consent_failed") { const why = sp.get("reason") || ""; toast({ tone: "warn", title: "Admin consent failed", subtitle: CONSENT_REASON[why] || (why ? `Microsoft answered ${why}.` : undefined) }); }
    sp.delete("microsoft"); sp.delete("reason");
    const q = sp.toString();
    window.history.replaceState(null, "", window.location.pathname + (q ? "?" + q : "") + window.location.hash);
  }, []);
  /* The next setup step, shown under the credentials (CC 0156): disabled until all
     three are saved. */
  const ready = msCredentialsSaved(detail);
  const granted = ready && consent?.granted_at ? new Date(consent.granted_at) : null;
  const lastErr = consent?.last_error ? (typeof consent.last_error === "string" ? consent.last_error : consent.last_error.message || "") : "";
  return (
    <Card style={{ padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n1 }}>Admin consent</div>
        <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>
          {!ready ? "Save the credentials above first." : granted && !isNaN(granted.getTime()) ? `Admin consent granted on ${friendlyDateShort(granted)}.` : "A Microsoft Entra administrator must grant consent before these permissions work."}
        </div>
        {ready && lastErr ? <div style={{ fontSize: 12, color: T.red1, marginTop: 4 }}>{lastErr}</div> : null}
      </div>
      {canWrite ? <Btn variant={granted ? "secondary" : "primary"} disabled={busy || !ready} onClick={() => { setBusy(true); void startMicrosoftConsent().finally(() => setBusy(false)); }}>{busy ? "Opening…" : granted ? "Grant again" : "Grant admin consent"}</Btn> : null}
    </Card>
  );
}

function CapabilityPill({ cap }: { cap: MsCapability }) {
  const t = useT();
  const s = String(cap.state ?? "");
  // The automatic check after consent or a credential save (CC 0158).
  if ((cap as { checking?: boolean }).checking === true) return <Pill tone="neutral">Checking with Microsoft…</Pill>;
  if (!s) return null;
  const map: Record<string, { tone: "success" | "warn" | "danger" | "neutral"; label: string }> = {
    connected: { tone: "success", label: t("settings.integrationDetail.capConnected", "Connected") },
    needs_consent: { tone: "warn", label: t("settings.integrationDetail.capNeedsConsent", "Needs admin consent") },
    error: { tone: "danger", label: t("settings.integrationDetail.capError", "Error") },
    // Never checked yet (CC 0135 §4): neutral, not an error.
    unverified: { tone: "neutral", label: "Not checked yet" },
    not_configured: { tone: "neutral", label: t("settings.integrationDetail.capNotConfigured", "Not configured") },
    off: { tone: "neutral", label: t("settings.integrationDetail.capOff", "Off") },
  };
  const m = map[s] ?? { tone: "neutral" as const, label: s };
  return <Pill tone={m.tone} dot={m.tone === "success"}>{m.label}</Pill>;
}

/* "Expires on <date>" under a stored secret (CC 0114 §3); red once the date has passed,
   amber inside 30 days. */
function SecretExpiry({ iso }: { iso: string }) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const days = Math.floor((d.getTime() - Date.now()) / 86400000);
  const past = days < 0, soon = !past && days <= 30;
  const color = past ? T.red1 : soon ? T.orange1 : T.soft;
  return (
    <p style={{ fontSize: 11.5, color, margin: "4px 0 0", fontWeight: past || soon ? 600 : 400 }}>
      {past ? `Expired on ${friendlyDateShort(d)}. Save a new secret to reconnect.` : `Expires on ${friendlyDateShort(d)}${soon ? ` (in ${days} day${days === 1 ? "" : "s"})` : ""}`}
    </p>
  );
}

/* One configuration field, rendered by declared type. */
/* ── Form-level validation (CC 0123 §2, every integration form) ─────────────
 * Each input is checked in the form, by its declared type or (where the backend
 * declares plain text) by what the key names: client and directory ids are GUIDs,
 * organizer / sender / mailbox / email keys are addresses, url keys are links. The
 * server keeps its own checks; a 400 INTEGRATION_CONFIG_INVALID it still returns is
 * shown under the field it names. */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function configFieldKind(f: { type?: string; fieldKey?: string }): "guid" | "email" | "url" | "date" | "secret" | "text" {
  const ty = String(f.type ?? "");
  if (ty === "guid" || ty === "email" || ty === "url" || ty === "date") return ty;
  const k = String(f.fieldKey ?? "").toLowerCase();
  if (/(^|_)(client_id|tenant_id|entra_tenant_id|application_id|directory_id)$/.test(k)) return "guid";
  if (/(organizer|sender|mailbox|email)(_address)?$/.test(k)) return "email";
  if (/(^|_)url$/.test(k)) return "url";
  if (ty === "password") return "secret";
  return "text";
}
export function configFieldError(f: { type?: string; fieldKey?: string; l?: string }, value: string): string {
  const v = value.trim();
  const kind = configFieldKind(f);
  if (kind === "secret") return v ? "" : "Enter the secret value, or cancel to keep the saved one.";
  if (!v) return "";
  if (kind === "guid" && !GUID_RE.test(v)) return "Must be a GUID, like 00000000-0000-0000-0000-000000000000.";
  if (kind === "email" && !EMAIL_RE.test(v)) return "Must be an email address.";
  if (kind === "url") { try { const u = new URL(v); if (u.protocol !== "https:" && u.protocol !== "http:") return "Must be a web address."; } catch { return "Must be a web address."; } }
  if (kind === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(v)) return "Must be a date.";
  return "";
}
/** `feature`: the feature a field belongs to, so the Save can route it (CC 0516, integration-config-save.ts). */
export type ConfigDraft = { value: string; err: string; label: string; feature?: string };
/** One form, one Save (CC 0123 §1): fields inside report their changes here
 *  instead of saving one by one. */
export const ConfigFormCtx = createContext<null | { set: (key: string, d: ConfigDraft | undefined) => void; serverErr: Record<string, string>; saving: boolean }>(null);
const FieldError = ({ text }: { text?: string }) => (text ? <p role="alert" style={{ fontSize: 11, color: T.red1, margin: "4px 0 0", lineHeight: 1.5 }}>{text}</p> : null);

export function ConfigField({ f, canWrite, provider, feature, onSaved }: { f: IntegInput; canWrite: boolean; provider?: string; feature?: string; onSaved?: (d: RegDetail) => void }) {
  const t = useT();
  const [val, setVal] = useState<string>(typeof f.filled === "string" ? f.filled : "");
  const [on, setOn] = useState<boolean>(f.filled === true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggleErr, setToggleErr] = useState("");
  /* The server's refusal of a standalone text save, shown under the field (CC 0516). Cleared on the next keystroke. */
  const [textErr, setTextErr] = useState("");
  const form = useContext(ConfigFormCtx);
  const storedText = typeof f.filled === "string" ? f.filled : "";
  const isDate = f.type === "date";
  const isText = !isDate && f.type !== "toggle" && f.type !== "select" && f.type !== "mapping";
  const dateIso = isDate ? (/^\d{4}-\d{2}-\d{2}$/.test(val) ? val : (() => { const d = new Date(val); return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10); })()) : "";
  const editable = !!provider && !!f.fieldKey && canWrite && !f.readonly;
  const dirty = editable && (isDate ? dateIso !== storedText.slice(0, 10) : isText && (f.configured ? editing : val !== storedText));
  const localErr = dirty ? configFieldError(f, isDate ? dateIso : val) : "";
  const shownErr = localErr || (f.fieldKey && form ? form.serverErr[f.fieldKey] : "") || textErr || "";
  useEffect(() => {
    if (!form || !f.fieldKey) return;
    form.set(f.fieldKey, dirty ? { value: isDate ? dateIso : val, err: localErr, label: f.l, ...(feature ? { feature } : {}) } : undefined);
  }, [form, f.fieldKey, dirty, val, dateIso, localErr]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (form && f.fieldKey) form.set(f.fieldKey, undefined); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const fieldLabel = t("settings.integrationDetail.fLFRequired.icu", "{label}{required, select, yes { *} other {}}", { label: f.l, required: f.required ? "yes" : "no" });
  // Locked = no write access OR a backend-provisioned display-only field
  // (stripe-fe handoff §3: OAuth-provisioned values are never user-editable).
  const locked = !canWrite || !!f.readonly;
  // Payments work-order 2026-07-24 Task 2: every config input renders its
  // description below it (what it is / provisioned-automatically / constraints).
  const desc = f.desc ? <p style={{ fontSize: 11, color: T.soft, margin: "4px 0 0", lineHeight: 1.5 }}>{f.desc}</p> : null;
  /* A feature's toggle saves on change through the per-field feature-setting route
     (CC 0167): PUT …/features/{feature}/settings/{fieldKey} with value "true" | "false".
     The switch flips back and shows the server's message when the save is refused. A
     toggle with no feature or field key has no route, so it is shown locked rather than
     flipping only locally. */
  const toggleSavable = !locked && !!provider && !!feature && !!f.fieldKey;
  const flip = async () => {
    if (!toggleSavable || saving) return;
    const next = !on; setOn(next); setSaving(true); setToggleErr("");
    try { await putIntegrationFeatureSetting(String(provider), String(feature), String(f.fieldKey), next ? "true" : "false"); }
    catch (e) { setOn(!next); setToggleErr(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };
  if (f.type === "toggle") return (
    <div>
      <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", cursor: toggleSavable && !saving ? "pointer" : "default", opacity: saving ? 0.6 : 1 }}>
        <Toggle on={on} onChange={toggleSavable && !saving ? () => { void flip(); } : undefined} />
        <span style={{ fontSize: 13, color: T.n1, fontWeight: 500 }}>{f.l}</span>
      </label>
      {desc ? <div style={{ paddingLeft: 42 }}>{desc}</div> : null}
      {toggleErr ? <div style={{ paddingLeft: 42 }}><FieldError text={toggleErr} /></div> : null}
    </div>
  );
  if (f.type === "date") {
    /* Expiry-style date (e.g. a client secret's expiry), YYYY-MM-DD. No min: recording a
       secret that has already expired is the case we must warn about (CC 0114 ask 1).
       Saves like the text fields: updateIntegrationConfig, then a fresh detail read. */
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(val) ? val : (() => { const d = new Date(val); return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10); })();
    const stored = typeof f.filled === "string" ? f.filled.slice(0, 10) : "";
    const canSaveDate = !!provider && !!f.fieldKey && !locked && iso !== stored;
    const saveDate = async () => {
      if (!canSaveDate || saving || !provider || !f.fieldKey) return;
      setSaving(true);
      try {
        await updateIntegrationConfig(provider, { config: { [f.fieldKey]: iso } });
        const rec = regFromResponse(await getIntegrationDetail(provider));
        if (rec && onSaved) onSaved(rec);
        toast({ tone: "success", title: `${f.l} saved` });
      } catch (e) { toast({ tone: "warn", title: `Couldn't save ${f.l}`, subtitle: e instanceof Error ? e.message : String(e) }); }
      finally { setSaving(false); }
    };
    return (
      <div>
        <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{fieldLabel}</label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="date" value={iso} disabled={locked}
            onChange={(e) => setVal(e.target.value)}
            style={{ flex: 1, minWidth: 0, height: 36, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.borderStrong}`, background: T.white, fontFamily: T.font, fontSize: 13, color: iso ? T.n1 : T.soft, boxSizing: "border-box" }} />
          {!form && !locked && provider && f.fieldKey ? <Btn size="sm" variant="primary" disabled={!canSaveDate || saving || !!localErr} onClick={() => { void saveDate(); }}>{saving ? "Saving…" : "Save"}</Btn> : null}
        </div>
        <FieldError text={shownErr} />
        {desc}
      </div>
    );
  }
  if (f.type === "select") return (
    <div>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{fieldLabel}</label>
      <select value={val} onChange={(e) => setVal(e.target.value)} disabled={locked}
        style={{ width: "100%", height: 36, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.borderStrong}`, background: T.white, fontFamily: T.font, fontSize: 13, color: val ? T.n1 : T.soft }}>
        {!val ? <option value="">{f.placeholder || t("settings.appRegistration.text6", "Choose…")}</option> : null}
        {val ? <option value={val}>{val}</option> : null}
      </select>
      {desc}
    </div>
  );
  if (f.type === "mapping") return (
    <div>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{fieldLabel}</label>
      {val ? (
        <div style={{ padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 4, background: T.white, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: T.n1 }}>{val}</span>
          <Btn variant="ghost" size="sm" icon={<Icon.edit size={12} />}>{t("common.actions.edit", "Edit")}</Btn>
        </div>
      ) : (
        <div style={{ padding: 12, border: `1px dashed ${T.n4}`, borderRadius: 4, background: T.white, fontSize: 13, color: T.soft }}>
          {t("settings.integrationDetail.configureAfterConnecting", "Configure after connecting")}</div>
      )}
      {desc}
    </div>
  );
  // Item 1 (addendum, kit 9620814f): a value the backend reports as `configured`
  // renders READ-ONLY with a "Change" button — never edit-in-place over a mask.
  // Change clears to an empty editable field so a fresh value is typed; Save
  // PATCHes ONLY the re-typed field via updateIntegrationConfig, then re-reads
  // the registration (no-fake-flip). Backend-provisioned (readonly) fields stay
  // locked with no Change button. Legacy per-integration fields (no provider)
  // keep the prior editable behavior untouched.
  const canChange = !!provider && !!f.fieldKey && canWrite && !f.readonly;
  /* CC 0516: a FEATURE's field on a provider PATCH /config does not accept (google's gmail_send
     sender_address) saves through the feature-settings route, trimmed; editable providers keep
     PATCH /config. Either way the registration is re-read before anything says saved, and a
     refusal keeps the typed value and shows the server's message under the field. */
  const save = async () => {
    if (!provider || !f.fieldKey || saving || !val.trim()) return;
    setSaving(true); setTextErr("");
    try {
      if (savesAsFeatureSetting(provider, feature)) await putIntegrationFeatureSetting(provider, String(feature), f.fieldKey, val.trim());
      else await updateIntegrationConfig(provider, { config: { [f.fieldKey]: val } });
      const rec = regFromResponse(await getIntegrationDetail(provider));
      if (rec && onSaved) onSaved(rec);
      toast({ tone: "success", title: t("settings.appRegistration.featureUpdated", "{name} updated", { name: f.l }) });
      setEditing(false);
    } catch (e) {
      setTextErr(refusalMessage(e));
      toast({ tone: "warn", title: t("settings.appRegistration.couldNotUpdate", "Couldn't update {name}", { name: f.l }), subtitle: refusalMessage(e) });
    }
    finally { setSaving(false); }
  };
  if (f.configured && canChange && !editing) {
    return (
      <div>
        <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{f.l}</label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0, height: 36, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.n6, display: "flex", alignItems: "center", gap: 8, fontFamily: f.type === "password" ? T.fontMono : T.font, fontSize: 13, color: T.n2 }}>
            {f.type === "password" ? <span style={{ color: T.soft, display: "inline-flex" }}><Icon.lock size={12} /></span> : null}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{typeof f.filled === "string" && f.filled ? f.filled : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}</span>
          </div>
          <Btn variant="secondary" size="sm" onClick={() => { setVal(""); setEditing(true); }}>{t("settings.integrationDetail.change", "Change")}</Btn>
        </div>
        {desc}
        {f.expiresAt ? <SecretExpiry iso={f.expiresAt} /> : null}
      </div>
    );
  }
  return (
    <div>
      <Input
        label={fieldLabel}
        value={val}
        onChange={(e: any) => { setVal(e.target.value); if (textErr) setTextErr(""); }}
        placeholder={editing ? t("settings.integrationDetail.enterNewValue", "Enter a new value") : f.placeholder}
        // Stored secrets arrive pre-masked server-side (first chars + bullets, e.g.
        // "q7X8Q~kP••••") — render those as text so the identifying prefix shows.
        // Only mask live keystrokes on a fresh/replaced secret.
        type={f.type === "password" && !val.includes("•") ? "password" : "text"}
        leading={f.type === "password" ? <Icon.lock size={12} /> : undefined}
        readOnly={locked && !editing}
      />
      <FieldError text={shownErr} />
      {/* Inside a form (CC 0123 §1) the page's one Save saves this field; only Cancel stays here. */}
      {canChange && (!f.configured || editing) && (!form || editing) ? (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          {!form ? <Btn size="sm" disabled={saving || !val.trim() || !!localErr} onClick={() => { void save(); }}>{saving ? t("settings.integrationDetail.saving", "Saving\u2026") : t("common.actions.save", "Save")}</Btn> : null}
          {editing ? <Btn variant="ghost" size="sm" disabled={saving || !!form?.saving} onClick={() => { setEditing(false); setVal(typeof f.filled === "string" ? f.filled : ""); }}>{t("common.actions.cancel", "Cancel")}</Btn> : null}
        </div>
      ) : null}
      {desc}
    </div>
  );
}

/* Graph API logging add-on config (kit ebcdff34, PART B) — the one feature-key-
 * gated add-on panel: shown only when the backend exposes a graph-logging
 * feature on the registration and it is enabled. Settings persist via the
 * dedicated graph-log ops (awaited, honest errors). */
const GRAPH_LOG_FEATURE_KEYS = ["graph_logging", "msgraphlog"]; // ui-config — feature keys that expose the Graph-log settings panel

const GRAPH_LOG_RETENTION_DAYS = [7, 30, 90, 180, 365]; // ui-config — the contract allows 1–365

function GraphLogPanel({ canWrite }: { canWrite: boolean }) {
  const t = useT();
  const nav = useNav();
  type GLSettings = { enabled: boolean; retentionDays: number; logBodies: boolean };
  const [settings, setSettings] = useState<GLSettings | null>(null);
  const [settingsErr, setSettingsErr] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  useEffect(() => {
    let alive = true;
    getGraphLogSettings().then((r: any) => { if (alive) setSettings(r.data); }).catch((e) => { if (alive) setSettingsErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, []);
  const saveSettings = async (patch: Partial<GLSettings>) => {
    if (savingSettings || !settings) return;
    setSavingSettings(true);
    try {
      const r: any = await updateGraphLogSettings(patch);
      setSettings(r.data);
      toast({ tone: "success", title: t("settings.integrationDetail.graphLogSettingsSaved", "Logging settings saved") });
    } catch (e) { toast({ tone: "warn", title: t("settings.integrationDetail.graphLogSettingsSaveFailed", "Couldn't save logging settings"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setSavingSettings(false); }
  };
  return (
    <Card style={{ padding: 0 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
        <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.integrationDetail.graphApiLogging", "Graph API logging")}</h3>
        <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.integrationDetail.graphApiLoggingSub", "Audit log of every Microsoft Graph call this registration makes.")}</p>
      </div>
      {/* Settings (operator 6 Oct): body capture AND how long logs are kept; on/off is
       * the feature row's own toggle. Both write updateGraphLogSettings. */}
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
        {settingsErr ? <span style={{ fontSize: 12, color: T.red1 }}>{settingsErr}</span> : !settings ? <span style={{ fontSize: 12, color: T.soft }}>{t("settings.integrationDetail.loadingSettings", "Loading settings…")}</span> : (
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: canWrite ? "pointer" : "default" }}>
            <span style={{ marginTop: 2, opacity: savingSettings ? 0.5 : 1, display: "inline-flex" }}>
              <Toggle on={settings.logBodies} onChange={canWrite && !savingSettings ? () => { void saveSettings({ logBodies: !settings.logBodies }); } : undefined} />
            </span>
            <span>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, display: "block" }}>{t("settings.integrationDetail.captureBodies", "Capture request & response bodies")}</span>
              <span style={{ fontSize: 12, color: T.soft, lineHeight: 1.5 }}>{t("settings.integrationDetail.captureBodiesSub", "May contain personal data (PII) — stored AES-256 encrypted, decrypted only when you open a log row.")}</span>
              <span style={{ fontSize: 12, color: T.soft, lineHeight: 1.5, display: "block", marginTop: 4 }}>{t("settings.integrationDetail.captureBodiesFromNow", "Applies to calls made from now on. Calls logged while this was off have no bodies.")}</span>
            </span>
          </label>
        )}
      </div>
      {settings ? (
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ flex: "1 1 240px", minWidth: 0 }}>
            <label htmlFor="graph-log-retention" style={{ fontSize: 13, fontWeight: 600, color: T.n1, display: "block" }}>{t("settings.integrationDetail.keepLogsFor", "Keep logs for")}</label>
            <span style={{ fontSize: 12, color: T.soft, lineHeight: 1.5 }}>{t("settings.integrationDetail.keepLogsForSub", "Older log rows, and their bodies, are deleted automatically.")}</span>
          </span>
          <select id="graph-log-retention" value={settings.retentionDays} disabled={!canWrite || savingSettings}
            onChange={(e) => { void saveSettings({ retentionDays: Number(e.target.value) }); }}
            style={{ height: 34, padding: "0 10px", borderRadius: 8, border: `1px solid ${T.border}`, background: canWrite ? "#fff" : T.n4, fontSize: 13, color: T.n1 }}>
            {(GRAPH_LOG_RETENTION_DAYS.includes(settings.retentionDays) ? GRAPH_LOG_RETENTION_DAYS : [...GRAPH_LOG_RETENTION_DAYS, settings.retentionDays].sort((x, y) => x - y)).map((d) => (
              <option key={d} value={d}>{d === 1 ? t("settings.integrationDetail.oneDay", "1 day") : t("settings.integrationDetail.nDays", "{n} days", { n: d })}</option>
            ))}
          </select>
        </div>
      ) : null}
      {can("audit.read") ? (
        <div style={{ padding: "10px 16px" }}>
          <Btn size="sm" variant="ghost" icon={<Icon.arrow size={12} />} onClick={() => nav.go("settings.audit", { tab: "graph" })}>{t("settings.integrationDetail.viewGraphLogs", "View Graph API logs")}</Btn>
        </div>
      ) : null}
      {/* Log viewer intentionally NOT rendered here (operator 2026-07-22): the
       * integration page carries only the SETTING. The full log table/stats/CSV
       * live behind the graph-logs ops for a dedicated observability surface. */}
    </Card>
  );
}

/* ─── Left column: shared credentials + "Features on this registration" ── */
export function RegistrationPanels({ detail, focusKey, canWrite, onDetail }: { detail: RegDetail; focusKey?: string | null; canWrite: boolean; onDetail: (d: RegDetail) => void }) {
  const t = useT();
  /* Google Calendar sync is a property of the DEPLOYMENT (the served
     `features.google_sync` flag), not of the provider record: a deployment
     that did not compose calendar sync has nowhere to send a stored key.
     Loading, absent or false all read as "not here" — the panels stay hidden
     until the server says otherwise. */
  const publicConfig = useTenantPublicConfig();
  const googleSyncComposed = publicConfig?.features?.google_sync === true;
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [graphLogOpen, setGraphLogOpen] = useState(false);
  // Microsoft layer (restored 2026-07-27): live capability status + verify.
  const isMs = detail.provider === MS_PROVIDER;
  const [statusNonce, setStatusNonce] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [verifyRes, setVerifyRes] = useState<MsVerifyResult | null>(null);
  const msCaps = useMicrosoftStatus(detail.provider, statusNonce);
  const msStatus = useMicrosoftStatusRecord(detail.provider, statusNonce);
  const [checking, setChecking] = useState(false);
  /* The Features list's Check (CC 0133): status WITHOUT probe=false runs one live check
     per enabled, configured capability and records it; the rows then re-read. */
  const doCheck = async () => {
    if (checking) return;
    setChecking(true);
    try { await getMicrosoftStatus({}); }
    catch (e) { toast({ tone: "warn", title: "Couldn't check the features", subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setChecking(false); setStatusNonce((n) => n + 1); }
  };
  /* ONE FORM, ONE SAVE (CC 0123 §1). Every field on the page reports its change
     here; the Save bar sends all of them in one call, one message for the whole
     save. Sender fields (CC 0132) go to the Microsoft settings route in the same Save.
     A refused field keeps its value and shows the server's message under it. */
  const [drafts, setDrafts] = useState<Record<string, ConfigDraft>>({});
  const [serverErr, setServerErr] = useState<Record<string, string>>({});
  const [formSaving, setFormSaving] = useState(false);
  const [formNonce, setFormNonce] = useState(0);
  /* The last draft each field reported, read synchronously so a server message is cleared only when the
     field's VALUE changes (CC 0517 on drop g). Fields re-report on every render (the form context is a new
     object each time), so clearing on every report wiped a refusal the moment the Save finished. */
  const lastDraft = useRef<Record<string, ConfigDraft | undefined>>({});
  const formApi = useRef({ set: (key: string, d: ConfigDraft | undefined) => {
    const prev = lastDraft.current[key];
    const valueChanged = d ? !prev || prev.value !== d.value : !!prev;
    lastDraft.current = { ...lastDraft.current, [key]: d };
    setDrafts((cur) => {
      if (!d) { if (!(key in cur)) return cur; const n = { ...cur }; delete n[key]; return n; }
      const p = cur[key];
      if (p && p.value === d.value && p.err === d.err && p.feature === d.feature) return cur;
      return { ...cur, [key]: d };
    });
    if (valueChanged) setServerErr((cur) => (key in cur ? (() => { const n = { ...cur }; delete n[key]; return n; })() : cur));
  } }).current;
  const draftKeys = Object.keys(drafts);
  const formInvalid = draftKeys.some((k) => drafts[k].err);
  const saveForm = async () => {
    if (formSaving || !draftKeys.length || formInvalid) return;
    setFormSaving(true);
    const configDrafts: { key: string; value: string; feature?: string }[] = []; const ms: Record<string, unknown> = {};
    draftKeys.forEach((k) => {
      // An unreadable draft is SKIPPED, never sent as [] (which clears every group; CC 0160 §2).
      if (k === "ms:groupIds") { try { const v = JSON.parse(drafts[k].value); if (Array.isArray(v)) ms.groupIds = v; } catch { /* skip */ } }
      else if (k.startsWith("ms:")) ms[k.slice(3)] = drafts[k].value.trim() || null;
      else configDrafts.push({ key: k, value: drafts[k].value, ...(drafts[k].feature ? { feature: drafts[k].feature } : {}) });
    });
    try {
      /* CC 0516: a feature's field on a provider PATCH /config refuses goes through the feature-settings
         route, one PUT per field (integration-config-save.ts); the rest is one PATCH /config as before. */
      const { refused } = configDrafts.length
        ? await saveConfigDrafts(detail.provider, configDrafts, { patchConfig: updateIntegrationConfig, putSetting: putIntegrationFeatureSetting })
        : { refused: {} as Record<string, string> };
      if (Object.keys(refused).length) {
        /* A refused field keeps its typed value and shows the server's message. The registration is not
           re-read here: that would remount the fields and drop what was typed. A field that did save is
           sent again with the next Save (the PUT is idempotent). */
        setServerErr(refused);
        toast({ tone: "warn", title: "Couldn't save the changes", subtitle: "See the highlighted fields." });
        return;
      }
      if (Object.keys(ms).length) await updateMicrosoftSettings(ms);
      const rec = regFromResponse(await getIntegrationDetail(detail.provider));
      if (rec) onDetail(rec);
      setDrafts({}); setServerErr({}); lastDraft.current = {}; setFormNonce((n) => n + 1); setStatusNonce((n) => n + 1);
      toast({ tone: "success", title: draftKeys.length === 1 ? `${drafts[draftKeys[0]].label} saved` : "Changes saved" });
    } catch (e) {
      const fieldsErr = (e as { fields?: { path?: string; field?: string; message?: string }[] } | null)?.fields;
      const named: Record<string, string> = {};
      (Array.isArray(fieldsErr) ? fieldsErr : []).forEach((x) => {
        const key = String(x.field ?? x.path ?? "").replace(/^config\./, "");
        const hit = draftKeys.find((k) => k === key || k === "ms:" + key);
        if (hit) named[hit] = x.message || "This value was refused.";
      });
      setServerErr(named);
      toast({ tone: "warn", title: "Couldn't save the changes", subtitle: Object.keys(named).length ? "See the highlighted fields." : (e instanceof Error ? e.message : String(e)) });
    } finally { setFormSaving(false); }
  };
  const feats = regFeatures(detail);
  const fields = (Array.isArray(detail.configFields) ? detail.configFields : []).filter(notRetired);
  // Item 3 (addendum): without shared credentials a feature can't be switched on.
  // Kept for the record version + the generic hint; it no longer gates the toggles
  // (CD docket 2026-09-15 item 2 — see doToggle's note).
  const requiresInput = regRequiresInput(detail);
  /* Which required fields are still empty, and which features they hold down
   * (CD docket 2026-09-15 item 3). The registration-wide "Enter credentials to
   * enable these features" line is misleading once the credentials ARE stored and
   * what's missing is a service identifier on a group — so when the backend names
   * the missing fields, name them instead, together with the features waiting on them. */
  const missingLabels = regMissingFieldLabels(detail);
  const waitingFeats = feats.filter((f) => f.credentialsSatisfied === false || f.status === "requires_input");
  const joinList = (xs: string[]) => (xs.length < 2 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} ${t("common.and", "and")} ${xs[xs.length - 1]}`);
  // Interpolations stay literal until their ICU keys ship (same rule as the PayPal copy).
  const missingHint = missingLabels.length
    ? `${t("settings.integrationDetail.enterMissingFields", "Enter the")} ${joinList(missingLabels)}${waitingFeats.length ? ` ${t("settings.integrationDetail.toEnableFeatures", "to enable")} ${joinList(waitingFeats.map((f) => f.name))}` : ""}.`
    : null;
  // Remount config fields whenever the backend record changes (e.g. after a
  // disconnect cascade clears values) so their local input state re-seeds from
  // the fresh configFields instead of holding stale text/masks.
  const recordVer = `${detail.updatedAt ?? ""}:${detail.status ?? ""}:${requiresInput}:${formNonce}`;
  const graphLogFeature = feats.find((f) => GRAPH_LOG_FEATURE_KEYS.includes(f.key));
  // Pre-focus (grid deep-link): tint the tapped service's row and bring it into
  // view by adjusting the nearest scrollable ancestor (never scrollIntoView).
  const focusRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const el = focusRef.current;
    if (!el) return;
    let p: HTMLElement | null = el.parentElement;
    while (p && p.scrollHeight <= p.clientHeight + 1) p = p.parentElement;
    if (p) p.scrollTop += el.getBoundingClientRect().top - p.getBoundingClientRect().top - 96;
  }, [focusKey, detail.provider]);
  // No-fake-interactivity: the toggle flips only from the awaited PATCH's
  // response (the recomputed IntegrationDetail); failures change nothing.
  /* The switch expresses the stored DECISION (requestedEnabled), which the feature-enable
   * route accepts whether or not the credentials are satisfied — `enabled` follows later,
   * once they are. So the decision is always safe to record, and a registration-wide
   * requires-input flag no longer holds every switch down (CD docket 2026-09-15 item 2:
   * the switches looked interactive, swallowed the click client-side, and said nothing).
   * The row's waiting line below carries the effective state in words. */
  const doToggle = async (f: RegFeature) => {
    if (busyKey || !canWrite) return;
    const next = !(f.requestedEnabled ?? f.enabled);
    setBusyKey(f.key);
    try {
      const r = await updateIntegrationFeature(detail.provider, f.key, { enabled: next });
      const rec = regFromResponse(r);
      if (rec) onDetail(rec);
      void refreshConnections(); // member catalog rows (grid pills) re-read the server
      setStatusNonce((n) => n + 1); // capability pills re-read after every toggle
      toast({ tone: "success", title: next ? t("settings.appRegistration.featureEnabledName", "{name} enabled", { name: f.name }) : t("settings.appRegistration.featureDisabledName", "{name} disabled", { name: f.name }) });
    } catch (e) {
      toast({ tone: "warn", title: next ? t("settings.appRegistration.couldNotEnable", "Couldn't enable {name}", { name: f.name }) : t("settings.appRegistration.couldNotDisable", "Couldn't disable {name}", { name: f.name }), subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setBusyKey(null); }
  };
  /* Verify connection (MS Part C, restored). Result is read from the payload's
   * `ok` / `error.code` — never the HTTP status; a transport-level rejection
   * renders as PROVIDER_UNREACHABLE, never a fake success. */
  const doVerify = async () => {
    if (verifying) return;
    setVerifying(true);
    setVerifyRes(null);
    try {
      const rec = unwrap(await verifyMicrosoftConnection()) as MsVerifyResult | null;
      setVerifyRes(rec ?? { ok: false, error: { code: "PROVIDER_UNREACHABLE" } });
    } catch (e) {
      setVerifyRes({ ok: false, error: { code: "PROVIDER_UNREACHABLE", message: e instanceof Error ? e.message : String(e) } });
    } finally {
      setVerifying(false);
      setStatusNonce((n) => n + 1); // capability pills re-read after every verify
    }
  };
  /* Grouped features (2026-08-04): a registration that declares API PRODUCTS
   * (Twilio: Programmable Messaging vs Conversations) renders one titled section per
   * product — the product's own service settings, then its channels. A registration
   * with no groups (Google, Microsoft) renders the same flat list as before. */
  const groupDefs: RegFeatureGroup[] = Array.isArray(detail.featureGroups) ? detail.featureGroups : [];
  const featureRows: { group?: RegFeatureGroup; feature?: RegFeature }[] = [];
  if (groupDefs.length) {
    const claimed: Record<string, boolean> = {};
    groupDefs.forEach((g) => {
      const members = feats.filter((f) => f.group === g.key);
      if (!members.length && !(Array.isArray(g.configFields) ? g.configFields.length : 0)) return;
      members.forEach((f) => { claimed[f.key] = true; });
      featureRows.push({ group: g });
      members.forEach((f) => featureRows.push({ feature: f }));
    });
    feats.filter((f) => !claimed[f.key]).forEach((f) => featureRows.push({ feature: f }));
  } else {
    feats.forEach((f) => featureRows.push({ feature: f }));
  }
  return (
    <ConfigFormCtx.Provider value={{ set: formApi.set, serverErr, saving: formSaving }}>
      <Card style={{ padding: 0 }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12 }}>
          <ProviderLogo id={detail.provider} name={detail.name} brand={detail.brand || T.n1} glyph={detail.icon} size={32} radius={8} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{detail.name}</h3>
            <p style={{ ...tx.help, marginTop: 2 }}>{detail.desc ?? ""}</p>
          </div>
          {/* Only where something IS shared at registration level. A per-company
              certificate signing per feature (Apple Pass) is not shared — saying so
              two lines under "nothing is shared across tenants" was the operator's
              compliance flag. */}
          {(detail.configFields?.length ?? 0) > 0
            ? <Pill tone="info">{t("settings.integrationDetail.sharedAcrossFeatures", "Shared across features")}</Pill>
            : null}
          {/* Verify lives in the status banner only (CC 0135 §2). */}
          {false ? (
            <Btn variant="secondary" size="sm" icon={<Icon.refresh size={12} />} disabled={verifying} onClick={() => { void doVerify(); }}>
              {verifying ? t("settings.integrationDetail.verifying", "Verifying…") : t("settings.integrationDetail.verifyConnection", "Verify connection")}
            </Btn>
          ) : null}
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Verify result banner — success shows the account it authenticated as;
            * failure maps the backend error CODE to actionable copy. */}
          {verifyRes ? (verifyRes.ok ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", padding: 12, borderRadius: 6, background: T.green2, border: `1px solid color-mix(in srgb, ${T.green1} 19%, transparent)` }}>
              <span style={{ width: 24, height: 24, borderRadius: 999, background: T.green1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.check size={12} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("settings.integrationDetail.connectionVerified", "Connection verified")}</div>
                <div style={{ fontSize: 12, color: T.n2 }}>
                  {[verifyRes.account?.label, verifyRes.account?.email].filter(Boolean).join(" · ")}
                  {typeof verifyRes.latency_ms === "number" ? t("settings.appRegistration.text7", "· {latency_ms} ms", { latency_ms: verifyRes.latency_ms }) : ""}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: 12, borderRadius: 6, background: `color-mix(in srgb, ${T.red1} 7%, ${T.white})`, border: `1px solid color-mix(in srgb, ${T.red1} 25%, transparent)` }}>
              <span style={{ width: 24, height: 24, borderRadius: 999, background: T.red1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.warn size={12} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("settings.integrationDetail.verifyFailed", "Verification failed")}</div>
                <div style={{ fontSize: 12, color: T.n2, lineHeight: 1.5 }}>
                  {(() => {
                    const m = MS_VERIFY_ERRORS[String(verifyRes.error?.code ?? "")];
                    return m ? t(m.k, m.l) : (verifyRes.error?.message || t("settings.integrationDetail.verifyUnknownError", "Unknown error"));
                  })()}
                </div>
              </div>
            </div>
          )) : null}
          {fields.map((f, i) => <ConfigField key={`${recordVer}-${i}`} f={toInput(f)} canWrite={canWrite} provider={detail.provider} onSaved={onDetail} />)}
          {/* Item 1 (addendum #2): the OAuth redirect URI flows OUT to the provider
           * console — render it read-only with Copy, visible even when disconnected. */}
          {detail.callback ? (
            <div>
              <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{detail.callbackLabel || t("settings.integrationDetail.redirectUri", "Redirect URI")}</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <code style={{ flex: 1, minWidth: 0, height: 36, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.n6, display: "flex", alignItems: "center", fontFamily: T.fontMono, fontSize: 12, color: T.p1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{detail.callback}</code>
                <Btn variant="secondary" size="sm" icon={<Icon.copy size={12} />} onClick={() => { try { navigator.clipboard.writeText(String(detail.callback)); } catch { /* ignore */ } toast({ tone: "success", title: t("settings.integrationDetail.copied", "Copied") }); }}>{t("settings.integrationDetail.copy", "Copy")}</Btn>
              </div>
              <p style={{ fontSize: 11, color: T.soft, margin: "4px 0 0", lineHeight: 1.5 }}>{detail.callbackHint || (detail.provider === MS_PROVIDER ? "Add this under the app registration's Authentication > Web redirect URIs in the Microsoft Entra admin center." : t("settings.integrationDetail.redirectUriHint", "Add this redirect URI to your provider console (Google Cloud / Azure) before connecting."))}</p>
            </div>
          ) : null}
          {detail.docs ? <p style={{ ...tx.help, margin: 0 }}>{detail.docs}</p> : null}
        </div>
      </Card>
      {/* Setup order (CC 0156): credentials, then admin consent as the next step, then features. */}
      {isMs ? <MicrosoftAdminConsent detail={detail} canWrite={canWrite} /> : null}
      <Card style={{ padding: 0 }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
          {isMs ? (
            <div style={{ float: "right", marginLeft: 12 }}>
              <Btn variant="secondary" size="sm" icon={<Icon.refresh size={12} />} disabled={checking || !msCredentialsSaved(detail)} onClick={() => { void doCheck(); }}>{checking ? "Checking…" : "Check"}</Btn>
            </div>
          ) : null}
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{detail.featuresTitle || t("settings.integrationDetail.featuresOnThisRegistration", "Features on this registration")}</h3>
          <p style={{ ...tx.help, marginTop: 2 }}>{detail.featuresSubtitle || t("settings.integrationDetail.eachFeatureIsSwitchedIndividually", "Credentials are shared — each feature is switched on or off individually.")}</p>
          {missingHint
            ? <p style={{ ...tx.help, marginTop: 4, color: T.orange1 }}>{missingHint}</p>
            : requiresInput ? <p style={{ ...tx.help, marginTop: 4, color: T.orange1 }}>{t("settings.integrationDetail.enterCredentialsToEnable", "Enter credentials to enable these features.")}</p> : null}
        </div>
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {featureRows.map((row, i) => {
            if (row.group) {
              const g = row.group;
              const gFields = (Array.isArray(g.configFields) ? g.configFields : []).filter(notRetired);
              return (
                <li key={`group-${g.key}`} style={{ padding: "12px 16px", borderTop: i ? `1px solid ${T.border}` : "none", background: T.n6, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{g.name ?? g.key}</div>
                    {g.description ? <p style={{ ...tx.help, marginTop: 2 }}>{g.description}</p> : null}
                  </div>
                  {gFields.length ? (
                    <div style={{ display: "grid", gap: 10, maxWidth: 520 }}>
                      {gFields.map((cf, ci) => <ConfigField key={`${recordVer}-${g.key}-${ci}`} f={toInput(cf)} canWrite={canWrite} provider={detail.provider} onSaved={onDetail} />)}
                    </div>
                  ) : null}
                </li>
              );
            }
            const f = row.feature as RegFeature;
            const focused = focusKey != null && f.key === focusKey;
            const cap = msCaps ? msCaps[MS_FEATURE_CAPABILITY[f.key] ?? ""] : undefined;
            // Add-ons (graph logging) have no Microsoft capability — the old MS page
            // showed no pill for them rather than the raw record status string.
            const hidePill = !cap && !!msCaps && MS_FEATURE_CAPABILITY[f.key] === undefined;
            return (
              <li key={f.key} ref={focused ? focusRef : undefined} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 16px", borderTop: i ? `1px solid ${T.border}` : "none", background: focused ? `color-mix(in srgb, ${T.p1} 4%, ${T.white})` : T.white }}>
                <ProviderLogo id={`${detail.provider}_${f.key}`} name={f.name} brand={detail.brand || T.n1} glyph={f.icon || detail.icon} size={28} radius={6} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{f.name}</span>
                    {/* Live capability state wins over the record's own status string. */}
                    {cap ? <CapabilityPill cap={cap} /> : hidePill ? null : <FeatureStatusPill status={f.status} label={f.statusLabel} />}
                    {/* Staff sign-in lives on Security › Single sign-on (CC 0222 §3); the two pages link each other. */}
                    {isMs && MS_FEATURE_CAPABILITY[f.key] === "user_sync" ? <SsoSettingsLink /> : null}
                  </div>
                  <div style={{ fontSize: 12, color: T.soft, marginTop: 2, lineHeight: 1.5 }}>{f.description ?? ""}</div>
                  {/* Per-row effective state in words: switched on, waiting on a named field.
                    * The badge alone says "Requires input" without saying for what. */}
                  {(f.requestedEnabled === true || f.enabled) && (f.credentialsSatisfied === false || f.status === "requires_input") ? (
                    <div style={{ fontSize: 11, color: T.orange1, marginTop: 4, lineHeight: 1.5 }}>
                      {/* CC 0535: the credentials wording only when the server says they are missing. Otherwise the
                        * SERVER's reason (`waitingReason`), as it sent it; with none, a neutral pointer to Recent
                        * activity. Nothing here guesses a reason. Not f.statusLabel: that is the pill's own word. */}
                      {f.credentialsSatisfied === false
                        ? (missingLabels.length
                          ? t("settings.appRegistration.text8", "{arg1} {arg2}", { arg1: t("settings.integrationDetail.onWaitingFor", "On — waiting for the"), arg2: joinList(missingLabels) })
                          : t("settings.integrationDetail.onWaitingForCredentials", "On — waiting for the required credentials"))
                        : f.waitingReason
                        ? `${t("settings.integrationDetail.onPrefix", "On —")} ${f.waitingReason}` /* the reason as sent: never through ICU, which would read its apostrophes and braces */
                        : t("settings.integrationDetail.onNotWorkingYet", "On — not working yet; see Recent activity for the reason")}
                    </div>
                  ) : null}
                  {cap?.state === "needs_consent" ? (
                    <div style={{ fontSize: 11, color: T.orange1, marginTop: 4, lineHeight: 1.5, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span>{t("settings.integrationDetail.grantAdminConsentHint", "Grant admin consent in the Microsoft Entra admin center to activate this permission.")}</span>
                      {/* The same action as the page's Admin consent card (CC 0115 ask 2). */}
                      {canWrite && msCredentialsSaved(detail) ? <Btn size="sm" variant="secondary" onClick={() => { void startMicrosoftConsent(); }}>Grant admin consent</Btn> : null}
                    </div>
                  ) : null}
                  {/* The reason a capability is waiting (CC 0135 §3): not_configured and
                      needs_consent show error.message too; unverified and connected show the note. */}
                  {(cap?.state === "not_configured" || cap?.state === "needs_consent") && cap?.error?.message ? (
                    <div style={{ fontSize: 11, color: T.orange1, marginTop: 4, lineHeight: 1.5 }}>{cap.error.message}</div>
                  ) : null}
                  {cap?.state === "error" && cap?.error?.message ? (
                    <div style={{ fontSize: 11, color: T.red1, marginTop: 4, lineHeight: 1.5 }}>{cap.error.message}</div>
                  ) : null}
                  {/* Mail.Send consent caveat: rendered even when the state is connected. */}
                  {cap?.note ? (
                    <div style={{ fontSize: 11, color: T.soft, fontStyle: "italic", marginTop: 4, lineHeight: 1.5 }}>{cap.note}</div>
                  ) : null}
                  {f.adminConsentRequired ? (
                    <div style={{ fontSize: 11, color: T.orange1, marginTop: 4, lineHeight: 1.5 }}>{t("settings.integrationDetail.adminConsentRequired", "Admin consent required")}</div>
                  ) : null}
                  {Array.isArray(f.scopes) && f.scopes.length ? (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                      {f.scopes.map((s) => <code key={s} style={{ fontFamily: T.fontMono, fontSize: 10, color: T.soft, background: T.n6, padding: "2px 6px", borderRadius: 3 }}>{s}</code>)}
                    </div>
                  ) : null}
                  <span style={{ flexShrink: 0, marginTop: 2, display: "none" }} />
                  {f.requiredApi ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 11, color: T.n2 }}>
                      <span style={{ color: T.soft, display: "inline-flex" }}><Icon.info size={11} /></span>
                      <span>{t("settings.integrationDetail.enableInProviderConsole", "Enable in the provider console:")}{" "}<strong style={{ color: T.n1, fontWeight: 600 }}>{f.requiredApi}</strong></span>
                    </div>
                  ) : null}
                  {/* A channel's own settings (its sender) live on the feature, not on the
                    * shared-credential card — shown once it is switched on, saved through the
                    * same PATCH /config surface. Fields carrying an `upload` block are
                    * FILE credentials (Apple Wallet cert + pass images) and render as the
                    * upload group; the certificate password rides that group too. */}
                  {/* Gated on the feature being REQUESTED on, not EFFECTIVE. The backend reports `enabled` as
                    * (decision AND required credentials present), so gating a credential-UPLOAD form on it
                    * deadlocks: the form is the only way to supply the certificate that makes `enabled` true.
                    * `requestedEnabled` is the stored decision; `status === "requires_input"` means exactly
                    * "switched on, credentials missing" — the state in which the form MUST render. */}
                  {(f.requestedEnabled === true || f.enabled || f.status === "requires_input")
                    && Array.isArray(f.configFields) && f.configFields.length ? (
                    f.configFields.some((cf) => !!(cf as Record<string, unknown>).upload) ? (
                      <div style={{ display: "grid", gap: 10, marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.border}`, maxWidth: 520 }}>
                        <FeatureCredentialUploads provider={String(detail.provider)} featureKey={f.key}
                          fields={f.configFields} canWrite={canWrite}
                          onSaved={() => { void (async () => {
                            const rec = regFromResponse(await getIntegrationDetail(detail.provider));
                            if (rec) onDetail(rec);
                          })(); }} />
                        {/* JAI-581: the three tenant-default pass colours are pulled OUT of the
                          * generic text-field map and rendered as the Appearance group below —
                          * they are one decision with one Save, one shared contrast advisory, and
                          * a null "inherit the platform default" state a text input cannot express.
                          * pass_background_image is NOT here: it is an image-asset and rides the
                          * upload group above with the icon and logo slots. */}
                        {f.configFields.filter((cf) => !(cf as Record<string, unknown>).upload
                            && (PASS_COLOR_KEYS as readonly string[]).indexOf(String((cf as Record<string, unknown>).key ?? "")) < 0
                            && !(String((cf as Record<string, unknown>).type ?? "") === "password" && /password/i.test(String((cf as Record<string, unknown>).key ?? ""))))
                          .map((cf, ci) => <ConfigField key={`${recordVer}-${f.key}-x-${ci}`} f={toInput(cf)} canWrite={canWrite} provider={detail.provider} feature={f.key} onSaved={onDetail} />)}
                        {f.configFields.some((cf) => (PASS_COLOR_KEYS as readonly string[]).indexOf(String((cf as Record<string, unknown>).key ?? "")) >= 0) ? (
                          <WalletAppearanceGroup key={`${f.key}-appearance`} provider={String(detail.provider)} feature={f} canWrite={canWrite} onSaved={onDetail} />
                        ) : null}
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 10, marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.border}`, maxWidth: 520 }}>
                        {f.configFields.filter(notRetired).map((cf, ci) => <ConfigField key={`${recordVer}-${f.key}-${ci}`} f={toInput(cf)} canWrite={canWrite} provider={detail.provider} feature={f.key} onSaved={onDetail} />)}
                      </div>
                    )
                  ) : null}
                  {/* Send email from (CC 0132): the Email sending feature's mailbox and
                      display name, saved by the page's one Save. */}
                  {isMs && MS_FEATURE_CAPABILITY[f.key] === "email" && (f.requestedEnabled === true || f.enabled) ? (
                    <MsSendFrom key={`sendfrom-${formNonce}`} status={msStatus} canWrite={canWrite} />
                  ) : null}
                  {/* Staff-sync groups (CC 0157), saved by the page's one Save. */}
                  {isMs && MS_FEATURE_CAPABILITY[f.key] === "user_sync" && (f.requestedEnabled === true || f.enabled) ? (
                    <MsEntraGroups key={`groups-${formNonce}`} canWrite={canWrite} ready={msCredentialsSaved(detail)} />
                  ) : null}
                </div>
                <span style={{ flexShrink: 0, marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
                  {graphLogFeature && f.key === graphLogFeature.key && f.enabled ? (
                    <Btn variant="secondary" size="sm" icon={<Icon.settings size={16} />} onClick={() => setGraphLogOpen((v) => !v)}>{graphLogOpen ? t("settings.integrationDetail.hideConfiguration", "Hide") : t("settings.integrationDetail.configure", "Configure")}</Btn>
                  ) : null}
                  <span style={{ opacity: busyKey === f.key ? 0.5 : 1, display: "inline-flex" }}>
                    <Toggle on={f.requestedEnabled ?? f.enabled} onChange={canWrite && busyKey == null ? () => { void doToggle(f); } : undefined} />
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </Card>
      {graphLogOpen && graphLogFeature?.enabled ? <GraphLogPanel canWrite={canWrite} /> : null}
      {/* Directory-sync panel (addendum #3): Google Workspace only — the gsuite/manage
       * ops are Google-specific; shows when the `directory` feature is enabled. */}
      {detail.provider === "google" && feats.some((f) => f.key === "directory" && f.enabled) ? <GsuiteDirectoryPanel canWrite={canWrite} /> : null}
      {/* Google Calendar connect (CC asks 1 and 4, 2026-09-18). Gated on the
          provider only, NOT on the `directory` feature: the service-account key
          and per-staff calendar sync are their own flow, and hiding the setup
          form behind Directory would mean a tenant that only wants calendars
          has no way to store a key. Also gated on the deployment declaring
          calendar sync (see `googleSyncComposed` above). */}
      {detail.provider === "google" && googleSyncComposed ? <GoogleServiceAccountForm canWrite={canWrite} /> : null}
      {detail.provider === "google" && googleSyncComposed ? <CalendarSyncStatusPanel /> : null}
      {draftKeys.length ? (
        <div data-testid="registration-save-bar" style={{ position: "sticky", bottom: 0, zIndex: 2, display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: formInvalid ? T.red1 : T.n2 }}>
            {formInvalid ? "Fix the highlighted fields to save." : `${draftKeys.length} unsaved change${draftKeys.length === 1 ? "" : "s"}: ${draftKeys.map((k) => drafts[k].label).join(", ")}`}
          </span>
          <Btn variant="primary" size="sm" disabled={formSaving || formInvalid || !canWrite} onClick={() => { void saveForm(); }}>{formSaving ? "Saving…" : "Save"}</Btn>
        </div>
      ) : null}
    </ConfigFormCtx.Provider>
  );
}

/* Send email from (CC 0132): `sender` and `senderDisplayName`, read from
   GET and saved with PATCH /v1/admin/integrations/microsoft/settings (CC, 2026-10-01). */
/* Staff-sync groups (CC 0157): search the tenant's Entra groups by display name
   (listMicrosoftEntraGroups, debounced), pick several, remove any. Saved ids the
   current list doesn't name stay visible by id. Stored with updateMicrosoftSettings
   { groupIds } through the page's one Save; [] clears the selection. */
/** The link from the Entra ID feature row to Settings › Security › Single sign-on. */
function SsoSettingsLink() {
  const nav = useNav();
  return (
    <button type="button" onClick={() => nav.go("settings.sso")} data-testid="ms-entra-sso-link"
      style={{ marginLeft: "auto", border: "none", background: "transparent", padding: 0, color: T.p1, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
      Single sign-on settings <Icon.arrow dir="right" size={11} />
    </button>
  );
}

function MsEntraGroups({ canWrite, ready }: { canWrite: boolean; ready: boolean }) {
  const form = useContext(ConfigFormCtx);
  /* `stored` stays null until the saved list has been READ; a failed read is an error
     state, never "no groups" (CC 0160 §1): saving a pick on top of a list that was never
     read would replace the real one and the next sync would drop those staff. */
  const [stored, setStored] = useState<string[] | null>(null);
  const [readErr, setReadErr] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [q, setQ] = useState("");
  /* Sync now (CC 0165): runs the staff sync on demand instead of waiting for the hourly run. */
  const [sync, setSync] = useState<{ phase: "idle" | "running" | "done" | "warn" | "error"; text: string }>({ phase: "idle", text: "" });
  const runSync = async () => {
    setSync({ phase: "running", text: "" });
    try {
      /* The route answers 200 for every outcome; data.status says what happened (CC 0171).
         Counts exist only for "ok"; any other status shows data.message as a warning. */
      const d = unwrap(await runMicrosoftUserSync()) ?? {};
      const status = typeof d.status === "string" ? d.status : "";
      if (status !== "ok") {
        const fallback: Record<string, string> = {
          skipped_disabled: "Staff sync is turned off.",
          application_required: "Connect the Microsoft app registration first.",
          no_groups: "No groups chosen.",
          needs_admin_consent: "Admin consent needed.",
          binding_missing: "The Microsoft connection is missing. Reconnect it, then try again.",
          error: "The sync did not run. Try again later.",
        };
        const msg = typeof d.message === "string" && d.message.trim() ? d.message : (fallback[status] ?? "The sync did not run.");
        setSync({ phase: "warn", text: msg });
        return;
      }
      const n = (k: string) => (typeof d[k] === "number" ? (d[k] as number) : null);
      const parts = [["created", n("created")], ["updated", n("updated")], ["deactivated", n("deactivated")]].filter(([, v]) => v !== null).map(([k, v]) => `${v} ${k}`);
      setSync({ phase: "done", text: parts.length ? `Sync finished: ${parts.join(", ")}.` : "Sync finished." });
    } catch (e) { setSync({ phase: "error", text: e instanceof Error ? e.message : String(e) }); }
  };
  const [res, setRes] = useState<{ phase: "idle" | "loading" | "ready" | "error"; groups: { id: string; displayName: string }[]; truncated: boolean; err: string }>({ phase: "idle", groups: [], truncated: false, err: "" });
  useEffect(() => {
    let alive = true;
    getMicrosoftSettings().then((r) => {
      if (!alive) return;
      const s = unwrap(r);
      const ids = Array.isArray(s?.groupIds) ? (s!.groupIds as unknown[]).map((x) => String(x).toLowerCase()) : [];
      setStored(ids); setPicked(ids); setReadErr(false);
    }).catch(() => { if (alive) { setStored(null); setReadErr(true); } });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!ready) return;
    let alive = true;
    setRes((r) => ({ ...r, phase: "loading", err: "" }));
    const h = setTimeout(() => {
      listMicrosoftEntraGroups({ search: q.trim() || undefined })
        .then((r: unknown) => {
          if (!alive) return;
          const d = unwrap(r) ?? {};
          const groups = Array.isArray(d.groups) ? (d.groups as { id: string; displayName: string }[]) : [];
          setNames((n) => { const m = { ...n }; groups.forEach((g) => { m[String(g.id).toLowerCase()] = g.displayName; }); return m; });
          setRes({ phase: "ready", groups, truncated: d.truncated === true, err: "" });
        })
        .catch((e: unknown) => { if (alive) setRes({ phase: "error", groups: [], truncated: false, err: e instanceof Error ? e.message : String(e) }); });
    }, 300);
    return () => { alive = false; clearTimeout(h); };
  }, [q, ready]);
  const same = stored !== null && picked.length === stored.length && picked.every((x) => stored.includes(x));
  useEffect(() => {
    if (stored === null) return;
    form?.set("ms:groupIds", same ? undefined : { value: JSON.stringify(picked), err: picked.length > 100 ? "Choose at most 100 groups." : "", label: "Staff-sync groups" });
  }, [picked, stored]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { form?.set("ms:groupIds", undefined); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id: string) => { if (stored === null) return; const k = id.toLowerCase(); setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k])); };
  return (
    <div style={{ display: "grid", gap: 10, marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.border}`, maxWidth: 560 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>Staff-sync groups</div>
      <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5 }}>Members of these Entra groups become staff.</div>
      {ready && canWrite && !readErr ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Btn type="button" variant="secondary" disabled={!stored?.length || sync.phase === "running"} onClick={() => { void runSync(); }} data-testid="ms-user-sync-run"
            title={!stored?.length ? "Save at least one group first." : undefined}>
            {sync.phase === "running" ? "Syncing…" : "Sync now"}
          </Btn>
          {!stored?.length ? <span style={{ fontSize: 11.5, color: T.soft }}>Save at least one group first.</span> : null}
          {sync.phase === "done" ? <span role="status" style={{ fontSize: 12, color: T.n1 }}>{sync.text}</span> : null}
          {sync.phase === "warn" ? <span role="status" style={{ fontSize: 12, color: T.n1, display: "inline-flex", alignItems: "center", gap: 6 }}><Icon.warn size={13} />{sync.text}</span> : null}
        </div>
      ) : null}
      {sync.phase === "error" ? <FieldError text={sync.text} /> : null}
      {!ready ? <div style={{ fontSize: 11.5, color: T.orange1 }}>Save the credentials above first.</div> : null}
      {readErr ? <FieldError text="Could not load the saved groups. Reload to try again." /> : null}
      {readErr ? null : picked.length ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {picked.map((id) => (
            <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 6px 3px 10px", fontSize: 12, color: T.n1, background: T.white, border: `1px solid ${T.border}`, borderRadius: 999 }}>
              {names[id] ?? id}
              {canWrite ? <button type="button" aria-label={`Remove ${names[id] ?? id}`} onClick={() => toggle(id)} style={{ display: "inline-flex", border: 0, background: "transparent", cursor: "pointer", padding: 2, color: T.soft }}><Icon.x size={12} /></button> : null}
            </span>
          ))}
        </div>
      ) : stored !== null ? <div style={{ fontSize: 11.5, color: T.soft }}>No groups chosen yet.</div> : null}
      {ready && canWrite && stored !== null ? (
        <>
          <Input label="Search groups" value={q} onChange={(e: any) => setQ(e.target.value)} placeholder="Type the start of a group name" maxLength={256} />
          {res.phase === "error" ? <FieldError text={res.err} /> : null}
          {res.phase === "loading" ? <div style={{ fontSize: 11.5, color: T.soft }}>Searching…</div> : null}
          {res.phase === "ready" ? (
            res.groups.length ? (
              <div style={{ display: "grid", gap: 2, maxHeight: 220, overflowY: "auto", border: `1px solid ${T.border}`, borderRadius: 8, padding: 4 }}>
                {res.groups.map((g) => {
                  const on = picked.includes(String(g.id).toLowerCase());
                  return (
                    <label key={g.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", fontSize: 12.5, color: T.n1, cursor: "pointer", borderRadius: 6 }}>
                      <input type="checkbox" checked={on} onChange={() => toggle(g.id)} />
                      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.displayName}</span>
                    </label>
                  );
                })}
              </div>
            ) : <div style={{ fontSize: 11.5, color: T.soft }}>No groups match.</div>
          ) : null}
          {res.phase === "ready" && res.truncated ? <div style={{ fontSize: 11.5, color: T.soft }}>More groups match. Type more of the name to narrow the list.</div> : null}
        </>
      ) : null}
      <FieldError text={form?.serverErr["ms:groupIds"]} />
    </div>
  );
}

function MsSendFrom({ canWrite }: { status?: unknown; canWrite: boolean }) {
  const form = useContext(ConfigFormCtx);
  const [s, setS] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    let alive = true;
    getMicrosoftSettings().then((r) => { if (alive) setS(unwrap(r)); }).catch(() => { if (alive) setS(null); });
    return () => { alive = false; };
  }, []);
  const storedSender = typeof s?.sender === "string" ? s.sender : "";
  const storedName = typeof s?.senderDisplayName === "string" ? s.senderDisplayName : "";
  const [sender, setSender] = useState(storedSender);
  const [name, setName] = useState(storedName);
  useEffect(() => { setSender(storedSender); setName(storedName); }, [storedSender, storedName]);
  const senderErr = sender.trim() && sender !== storedSender ? configFieldError({ type: "email" }, sender) : "";
  useEffect(() => { form?.set("ms:sender", sender !== storedSender ? { value: sender, err: senderErr, label: "Send email from" } : undefined); }, [sender, storedSender, senderErr]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { form?.set("ms:senderDisplayName", name !== storedName ? { value: name, err: "", label: "Sender name" } : undefined); }, [name, storedName]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { form?.set("ms:sender", undefined); form?.set("ms:senderDisplayName", undefined); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ display: "grid", gap: 10, marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.border}`, maxWidth: 520 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>Send email from</div>
      {!storedSender ? <div style={{ fontSize: 11, color: T.orange1, lineHeight: 1.5 }}>Add a mailbox to send email through Microsoft 365.</div> : null}
      <div>
        <Input label="Mailbox address" value={sender} onChange={(e: any) => setSender(e.target.value)} placeholder="name@yourdomain.com" readOnly={!canWrite} />
        <FieldError text={senderErr || form?.serverErr["ms:sender"]} />
      </div>
      <div>
        <Input label="Display name" value={name} onChange={(e: any) => setName(e.target.value)} placeholder="e.g. Student Services" readOnly={!canWrite} />
        <p style={{ fontSize: 11, color: T.soft, margin: "4px 0 0", lineHeight: 1.5 }}>Recipients see the sender mailbox's name from Microsoft 365. To show a different name, send from a shared mailbox with that name, or rename the mailbox in Microsoft 365.</p>
        <FieldError text={form?.serverErr["ms:senderDisplayName"]} />
      </div>
    </div>
  );
}

/* ─── Right column: aggregated scopes across the ENABLED features ───────── */
export function RegistrationScopes({ detail }: { detail: RegDetail }) {
  const t = useT();
  /* What Microsoft has GRANTED (CC 0123 §4): once consent exists, each row is marked
     Granted / Not granted from admin_consent.scopes; before it, the requested wording stays. */
  const msRec = useMicrosoftStatusRecord(detail.provider, 0);
  const consent = (msRec?.admin_consent ?? null) as { granted_at?: string | null; scopes?: string[] } | null;
  const granted = consent && consent.granted_at && Array.isArray(consent.scopes) ? new Set(consent.scopes.map((s) => s.toLowerCase())) : null;
  // Every feature contributes its scopes; the ones only a SWITCHED-OFF feature asks
  // for render dimmed as "not requested" rather than disappearing, so the admin
  // sees the full consent surface a feature would need before switching it on.
  // "On" is the admin's decision (requestedEnabled), not `enabled`: the contract's
  // enabled is requestedEnabled && credentialsSatisfied, and a feature waiting for
  // credentials or consent does request its scope (CC 0521).
  type ScopeRow = { scope: string; on: string[]; off: string[]; adminConsent: boolean };
  const rows: ScopeRow[] = [];
  regFeatures(detail).forEach((f) => (f.scopes ?? []).forEach((s) => {
    let row = rows.find((x) => x.scope === s);
    if (!row) { row = { scope: s, on: [], off: [], adminConsent: false }; rows.push(row); }
    const on = f.requestedEnabled ?? f.enabled;
    (on ? row.on : row.off).push(f.name);
    if (on && f.adminConsentRequired) row.adminConsent = true;
  }));
  return (
    <Card style={{ padding: 20 }}>
      <div style={tx.label as CSSProperties}>{detail.scopesTitle || t("settings.integrationDetail.permissionsScopes", "Permissions / scopes")}</div>
      <p style={{ ...tx.help, marginTop: 4 }}>{detail.scopesHint ? detail.scopesHint : detail.provider === MS_PROVIDER
        ? t("settings.integrationDetail.scopesFollowEnabledFeatures", "Requested from Microsoft based on which features are enabled — switching a feature off drops its scopes from the consent.")
        : t("settings.integrationDetail.grantedByEnabledFeatures", "Requested by the features enabled on this registration.")}</p>
      {rows.length === 0 ? (
        <div style={{ marginTop: 12, padding: 12, border: `1px dashed ${T.n4}`, borderRadius: 6, fontSize: 12, color: T.soft }}>
          {t("settings.integrationDetail.noFeaturesEnabledYet", "No features enabled yet — switch one on to see the permissions it requires.")}</div>
      ) : (
        <ul style={{ listStyle: "none", margin: "12px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
          {rows.map(({ scope, on, off, adminConsent }) => {
            const meta = scopeMeta(scope);
            const active = on.length > 0;
            return (
              <li key={scope} style={{ display: "flex", flexDirection: "column", gap: 4, opacity: active ? 1 : 0.55 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ color: active ? T.green1 : T.soft, display: "inline-flex" }}><Icon.check size={12} /></span>
                  <code style={{ fontFamily: T.fontMono, fontSize: 11, fontWeight: 600, color: T.n1 }}>{scope}</code>
                  {meta ? <Pill tone="neutral">{meta.delegated ? t("settings.integrationDetail.permissionTypeDelegated", "Delegated") : t("settings.integrationDetail.permissionTypeApplication", "Application · admin approval")}</Pill> : null}
                  {granted ? (granted.has(scope.toLowerCase()) ? <Pill tone="success">Granted</Pill> : <Pill tone="warn">Not granted</Pill>) : adminConsent ? <Pill tone="warn">{t("settings.integrationDetail.adminConsentRequired", "Admin consent required")}</Pill> : null}
                  {meta?.optional ? <Pill tone="neutral">{t("settings.integrationDetail.scopeOptional", "Optional")}</Pill> : null}
                </div>
                {meta?.note ? <div style={{ fontSize: 11, color: T.n2, lineHeight: 1.5, paddingLeft: 20 }}>{meta.note}</div> : null}
                <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.5, paddingLeft: 20 }}>
                  {active ? on.join(", ") : t("settings.appRegistration.text9", "{arg1} — {arg2}", { arg1: off.join(", "), arg2: t("settings.integrationDetail.notRequestedFeatureOff", "not requested (feature off)") })}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/* ─── Right column: recent activity from the registration record ────────── */
type ActRow = { e: string; at: string };
/* "Oct 2, 2026 · 7:11 AM" (CC 0155), in the page's date helpers; an unreadable value prints as sent. */
const actWhen = (at: string): string => { const d = at ? new Date(at) : null; return d && !isNaN(d.getTime()) ? `${friendlyDateShort(d)} · ${friendlyTime(d)}` : at; };
const ACT_PAGE = 10;
const actRows = (src: RegDetail | null | undefined): ActRow[] =>
  (Array.isArray(src?.activity) ? (src as RegDetail).activity ?? [] : [])
    .map((a) => ({ e: String(a.e ?? a.detail ?? ""), at: String(a.at ?? a.occurredAt ?? "") }))
    .filter((a) => a.e);

export function RegistrationActivity({ detail }: { detail: RegDetail }) {
  const t = useT();
  const isMs = detail.provider === MS_PROVIDER;
  // Categorized activity (operator 2026-07-22, restored 2026-07-27): each
  // Microsoft feature's rows are read from its OWN backend record and grouped
  // under a category title — nothing invented UI-side.
  const [groups, setGroups] = useState<{ key: string; title: string; rows: ActRow[] }[] | null>(null);
  const [loadingGroups, setLoadingGroups] = useState(isMs);
  useEffect(() => {
    if (!isMs) { setGroups(null); setLoadingGroups(false); return; }
    let alive = true;
    setLoadingGroups(true);
    void (async () => {
      /* The registration's own record first (CC 0128): credential saves, disconnects,
         admin-consent results, feature switches and Verify. */
      const own = actRows(detail);
      const out: { key: string; title: string; rows: ActRow[] }[] = own.length ? [{ key: "registration", title: "Registration", rows: own }] : [];
      for (const cat of MS_ACTIVITY_CATEGORIES) {
        const rows: ActRow[] = [];
        for (const member of cat.members) {
          const rec = regFromResponse(await getIntegrationDetail(member).catch(() => null));
          rows.push(...actRows(rec));
        }
        if (rows.length) out.push({ key: cat.key, title: cat.title, rows });
      }
      if (alive) { setGroups(out); setLoadingGroups(false); }
    })();
    return () => { alive = false; };
  }, [isMs, detail.provider, detail.updatedAt, detail.status, detail.activity]);
  const flat = actRows(detail);
  /* The newest 10 rows per group, then "Show more" adds 10 (CC 0155). Rows are sorted newest first. */
  const [shown, setShown] = useState<Record<string, number>>({});
  const newest = (rows: ActRow[]) => [...rows].sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0));
  const more = (key: string, total: number) => {
    const n = shown[key] ?? ACT_PAGE;
    return total > n ? (
      <button type="button" onClick={() => setShown((s) => ({ ...s, [key]: n + ACT_PAGE }))} style={{ display: "block", width: "100%", padding: "8px 16px", fontSize: 12, fontWeight: 600, color: T.p1, background: "transparent", border: 0, borderTop: `1px solid ${T.border}`, cursor: "pointer", textAlign: "left" }}>
        Show more ({total - n} more)
      </button>
    ) : null;
  };
  const empty = isMs ? !loadingGroups && (groups?.length ?? 0) === 0 : flat.length === 0;
  /* CC 0525: a row's message wraps in full (these are instructions the admin must read), and the time
     keeps its place at the top right. Before, the nowrap text could not shrink, so a long message pushed
     the time out of view and widened the card and the page. */
  const msgStyle: CSSProperties = { flex: "1 1 auto", minWidth: 0, fontSize: 12, color: T.n1, whiteSpace: "normal", overflowWrap: "anywhere" };
  const whenStyle: CSSProperties = { fontSize: 11, color: T.soft, flexShrink: 0, alignSelf: "flex-start" };
  return (
    <Card style={{ padding: 0, minWidth: 0 }} data-testid="registration-activity">
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
        <div style={tx.label as CSSProperties}>{t("settings.integrationDetail.recentActivity", "Recent activity")}</div>
      </div>
      {isMs && loadingGroups ? (
        <div style={{ padding: 20, fontSize: 12, color: T.soft, textAlign: "center" }}>{t("settings.integrationDetail.loadingActivity", "Loading activity…")}</div>
      ) : empty ? (
        <div style={{ padding: 20, fontSize: 12, color: T.soft, textAlign: "center" }}>
          {t("settings.integrationDetail.noActivityYetConnectToStartLoggingEvents", "No activity yet — connect to start logging events.")}</div>
      ) : isMs && groups ? (
        <div>
          {groups.map((g, gi) => (
            <section key={g.key} style={{ borderTop: gi ? `1px solid ${T.border}` : "none" }}>
              <div style={{ padding: "10px 16px 4px", fontSize: 11, fontWeight: 700, color: T.n1, letterSpacing: 0.2 }}>{t(`settings.integrationDetail.activityCat.${g.key}`, g.title)}</div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {newest(g.rows).slice(0, shown[g.key] ?? ACT_PAGE).map((a, i) => (
                  <li key={`${g.key}-${i}`} style={{ padding: "8px 16px", display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <span style={msgStyle}>{a.e}</span>
                    <span style={whenStyle}>{actWhen(a.at)}</span>
                  </li>
                ))}
              </ul>
              {more(g.key, g.rows.length)}
            </section>
          ))}
        </div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {newest(flat).slice(0, shown.flat ?? ACT_PAGE).map((a, i) => (
            <li key={i} style={{ padding: "10px 16px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
              <span style={msgStyle}>{a.e}</span>
              <span style={whenStyle}>{actWhen(a.at)}</span>
            </li>
          ))}
          {more("flat", flat.length) ? <li style={{ listStyle: "none" }}>{more("flat", flat.length)}</li> : null}
        </ul>
      )}
    </Card>
  );
}


/* ───────────────────────────────────────────────────────────────────────────
 * WalletAppearanceGroup (JAI-581) — tenant-default pass colours.
 *
 * Composes jrni-ui's ColorPicker rather than hand-rolling a hex input: a third
 * hex field sitting next to the primitive it duplicates is the fork the design
 * system exists to prevent. Two things the primitive does not carry, both held
 * OUTSIDE it rather than routed around it, and both worth raising against the
 * library since the next colour surface hits the same wall:
 *   1. No null state. `value` is a required string, so "unset / inherit the
 *      default" has no representation. The Use default control below owns that,
 *      and the picker is only mounted once a colour exists.
 *   2. Its hex input accepts 3-digit shorthand and normalises it. Harmless
 *      here — it always EMITS #RRGGBB, which is all the backend sees — but it
 *      means the tolerant reading lives in the input affordance, not the wire.
 * Its built-in `contrastAgainst` indicator IS wired (2026-08-20), one pair per
 * open picker, against the counterpart that colour is read against on the pass:
 * text and label against the effective background, the background against the
 * resolved label colour. That is live per-keystroke feedback while choosing. It
 * does not replace the aggregated notice below — that one is measured across all
 * three pass styles and lands after the fact, which is a different question.
 *
 * Open/closed state is held here too, not in the primitive: it has no `open` /
 * `onClose`, correctly, because Journey Builder branding mounts it in an
 * always-visible panel. Behind a collapsible row the mounting screen owes the
 * dismissal — see `openKey`.
 * ─────────────────────────────────────────────────────────────────────────── */

const PASS_COLOR_LABELS: Record<PassColorKey, string> = {
  pass_background_color: "Pass background colour",
  pass_foreground_color: "Pass text colour",
  pass_label_color: "Pass label colour",
};
const PASS_STYLE_NAMES: Record<string, string> = { eventTicket: "Event ticket", appointment: "Appointment", queue: "Queue position" };

export function WalletAppearanceGroup({ provider, feature, canWrite, onSaved }: {
  provider: string; feature: RegFeature; canWrite: boolean; onSaved: (d: RegDetail) => void;
}) {
  const t = useT();
  /* State model: we hold ONLY the keys the operator has actually edited, and
   * derive the effective value per key as "edited if edited, else stored".
   *
   * The obvious model — copy `stored` into a draft and re-seed it with an
   * effect when the record changes — is wrong here, and was the defect: `stored`
   * is recomputed from `feature.configFields` on every render, and the record
   * legitimately changes underneath this component (a sibling field saving, a
   * feature toggle, the post-save re-read). Any of those re-seeds fired
   * mid-edit and silently discarded the colour the operator had just committed,
   * leaving the picker displaying one value while the draft held another —
   * Save then persisted the stale one, or went inert because `dirty` computed
   * false against a draft that had been reset to match `stored`.
   *
   * With edits held separately there is nothing to clobber: a record change
   * updates the baseline, edits survive it, and `dirty` is simply "are there
   * any edits that differ from the baseline". */
  const stored = readPassAppearance(feature.configFields);
  const [edits, setEdits] = useState<Partial<PassAppearance>>({});
  const [saving, setSaving] = useState(false);
  /* Which field's picker is EXPANDED. Open/closed state belongs HERE, not in
   * ColorPicker: the primitive is a presentational inline panel with no `open` /
   * `onClose` in its prop surface (correct for Journey Builder branding, which
   * mounts it in an always-visible panel), so the screen that mounts it behind
   * "Set a colour" owns dismissing it. Open-ness used to be IMPLIED by "a colour
   * exists", which left the panel expanded forever with only the destructive Use
   * default underneath. Done collapses and KEEPS the choice. Deliberately not
   * auto-collapsing on change: dragging the saturation area and typing a hex both
   * emit onChange continuously, so collapse-on-change would close mid-drag. */
  const [openKey, setOpenKey] = useState<PassColorKey | null>(null);
  /* Mirror of `edits` that Save reads instead of its render closure. The hex
   * field commits on blur, and that blur is usually the same interaction that
   * presses Save — clicking the button blurs the field. Commit and click are
   * separate events, so a closure captured before the commit still held the
   * previous colour and Save persisted THAT while the field showed what the
   * operator had just typed. The ref is written synchronously inside `set`, so
   * it is correct the instant ColorPicker's onChange returns. */
  const editsRef = useRef<Partial<PassAppearance>>({});

  const valueOf = (k: PassColorKey): string | null => (k in edits ? (edits[k] ?? null) : stored[k]);
  const draft: PassAppearance = {
    pass_background_color: valueOf("pass_background_color"),
    pass_foreground_color: valueOf("pass_foreground_color"),
    pass_label_color: valueOf("pass_label_color"),
  };
  const changed = PASS_COLOR_KEYS.filter((k) => k in edits && draft[k] !== stored[k]);
  const dirty = changed.length > 0;
  const notice = passContrastNotice(draft);
  const eventTicket = PASS_STYLE_DEFAULTS.find((d) => d.issued) ?? PASS_STYLE_DEFAULTS[0];

  const set = (k: PassColorKey, v: string | null) => {
    const next = { ...editsRef.current, [k]: v };
    editsRef.current = next;
    setEdits({ ...next });
  };

  const save = async () => {
    if (saving || !canWrite) return;
    /* Read the edits from the REF, never from this render's closure. The hex
     * field commits on blur, and the blur that commits it is usually the same
     * interaction that presses Save — clicking the button blurs the field. The
     * commit and the click are separate events, so a closure captured before the
     * commit still held the previous colour: Save then persisted THAT while the
     * field on screen showed what the operator had just typed. Writing a value
     * the operator can see is not the one they entered is the worst failure this
     * control has. */
    const live = editsRef.current;
    const liveDraft: PassAppearance = {
      pass_background_color: "pass_background_color" in live ? (live.pass_background_color ?? null) : stored.pass_background_color,
      pass_foreground_color: "pass_foreground_color" in live ? (live.pass_foreground_color ?? null) : stored.pass_foreground_color,
      pass_label_color: "pass_label_color" in live ? (live.pass_label_color ?? null) : stored.pass_label_color,
    };
    /* Strict #RRGGBB validated before sending: the backend rejects anything else
     * rather than normalising it, so nothing mid-keystroke or partial reaches the
     * wire. Clearing sends an explicit null, never "" — the backend keeps the two
     * distinguishable ("inherit the default" vs "blank this") and "" fails
     * validation. Clear-to-empty is local state precisely so the invalid request
     * cannot be constructed. */
    const writes: { key: PassColorKey; value: string | null }[] = [];
    for (const k of PASS_COLOR_KEYS) {
      if (!(k in live)) continue;
      const v = liveDraft[k];
      if (v === stored[k]) continue;
      if (v !== null && !isPassHex(v)) {
        toast({ tone: "warn", title: t("settings.appRegistration.notHexColour", "{label} isn't a six-digit hex colour", { label: PASS_COLOR_LABELS[k] }), subtitle: t("settings.appRegistration.subtitle", "Use #RRGGBB — the server accepts nothing else.") });
        return;
      }
      writes.push({ key: k, value: v === null ? null : v.toUpperCase() });
    }
    if (!writes.length) {
      // The button is deliberately never disabled (see its comment), so a click
      // with nothing pending has to answer rather than look broken.
      toast({ tone: "info", title: t("settings.appRegistration.title5", "No appearance changes to save") });
      return;
    }
    setSaving(true);
    try {
      /* ONE PUT PER FIELD — the feature-settings route has no batch form, so a
       * three-colour save is three requests and the set is NOT atomic. They run
       * sequentially rather than in parallel so a failure stops at a known point:
       * everything before it is committed, everything after it untouched. The
       * re-read below then shows exactly what landed, so a partial failure
       * reports honestly instead of leaving the screen claiming a whole set was
       * saved. `updateIntegrationConfig` is NOT usable here — it 400s
       * CONFIG_NOT_SUPPORTED for apple and writes a store with no concept of
       * features (platform-confirmed 2026-08-13). */
      let done = 0;
      try {
        for (const w of writes) {
          await putIntegrationFeatureSetting(provider, feature.key, w.key, w.value);
          done += 1;
        }
      } catch (e) {
        const rec = regFromResponse(await getIntegrationDetail(provider).catch(() => null));
        if (rec) onSaved(rec);
        // Keep the edits: the operator's unsaved colours are still theirs to retry.
        toast({
          tone: "warn",
          title: done ? `Saved ${done} of ${writes.length} colours` : "Couldn't update pass appearance",
          subtitle: e instanceof Error ? e.message : String(e),
        });
        return;
      }
      const rec = regFromResponse(await getIntegrationDetail(provider));
      if (rec) onSaved(rec);
      // Cleared only after the re-read lands, so the group falls back to the
      // freshly-stored values rather than flashing the old baseline.
      editsRef.current = {};
      setEdits({});
      toast({ tone: "success", title: t("settings.appRegistration.title6", "Pass appearance updated") });
    } catch (e) {
      toast({ tone: "warn", title: t("settings.appRegistration.title7", "Couldn't update pass appearance"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ display: "grid", gap: 12, marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.border}` }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("settings.appRegistration.text10", "Appearance")}</div>
        <p style={{ fontSize: 11, color: T.soft, margin: "4px 0 0", lineHeight: 1.5 }}>
          {t("settings.appRegistration.text11", "One colour set applies to every pass style. Anything left on the default inherits the platform colour, which differs per style.")}</p>
      </div>

      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        {PASS_COLOR_KEYS.map((k) => {
          const v = draft[k];
          const fallback = k === "pass_background_color" ? eventTicket.bg : k === "pass_foreground_color" ? eventTicket.fg : resolvePassColors(eventTicket, draft).label;
          const open = openKey === k && v !== null;
          /* The picker's own contrast indicator, wired to the pair the colour is
           * actually read against on the pass: text and label sit ON the
           * background, and the background is judged against the resolved label
           * colour. Live per-keystroke feedback while choosing. The amber banner
           * below is untouched - backend guidance, advisory by design. */
          const against = k === "pass_background_color"
            ? resolvePassColors(eventTicket, draft).label
            : (draft.pass_background_color ?? eventTicket.bg);
          return (
            <div key={k} style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
              {open ? (
                <>
                  <ColorPicker value={v as string} onChange={(hex) => set(k, hex)} label={PASS_COLOR_LABELS[k]} disabled={!canWrite} contrastAgainst={against} style={{ width: "100%" }} />
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {/* Keep-and-collapse. The chosen colour stays in the draft and the
                      * row below shows it as a swatch + hex, matching how a field that
                      * was already set renders. Saving is still the group's one Save. */}
                    <Btn variant="secondary" size="sm" disabled={!canWrite} onClick={() => setOpenKey(null)}>{t("settings.appRegistration.text12", "Done")}</Btn>
                    <Btn variant="ghost" size="sm" disabled={!canWrite} onClick={() => { set(k, null); setOpenKey(null); }}>{t("settings.appRegistration.text13", "Use default")}</Btn>
                  </div>
                </>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: T.soft, letterSpacing: "0.02em" }}>{PASS_COLOR_LABELS[k]}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", border: `1px solid ${T.border}`, borderRadius: 6, background: T.white }}>
                    <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 4, background: v ?? fallback, border: `1px solid ${T.border}`, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: v ? T.n1 : T.soft, fontFamily: v ? T.fontMono : T.font, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v ?? t("settings.appRegistration.text14", "Platform default")}</span>
                  </div>
                  {v === null ? (
                    <Btn variant="secondary" size="sm" disabled={!canWrite} onClick={() => { set(k, fallback); setOpenKey(k); }}>{t("settings.appRegistration.text15", "Set a colour")}</Btn>
                  ) : (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Btn variant="secondary" size="sm" disabled={!canWrite} onClick={() => setOpenKey(k)}>{t("settings.appRegistration.text16", "Change")}</Btn>
                      <Btn variant="ghost" size="sm" disabled={!canWrite} onClick={() => set(k, null)}>{t("settings.appRegistration.text13", "Use default")}</Btn>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Advisory only — never a save blocker. The backend does not validate
        * contrast and has no plans to, so a blocking client check would enforce
        * a rule production does not and split design time from production on
        * what is acceptable. */}
      {notice ? (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", borderRadius: 6, background: notice.kind === "defect" ? T.orange2 : T.n6, border: `1px solid ${notice.kind === "defect" ? T.orange1 : T.border}` }}>
          <Icon.warn size={13} />
          <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: T.n2 }}>
            {notice.kind === "defect"
              ? t("settings.appRegistration.text17", "Low contrast on the event ticket — {arg1}:1 between the label colour and the background, under the {PASS_CONTRAST_MIN}:1 guideline. That is the pass a guest receives today.", { arg1: notice.ratio.toFixed(1), PASS_CONTRAST_MIN })
              : t("settings.appRegistration.text18", "Label colour is unset, so it falls back to a different default per pass style — on {arg1} that's {arg2}:1 against your background. Setting a label colour resolves it.", { arg1: PASS_STYLE_NAMES[notice.styleId] ?? notice.styleId, arg2: notice.ratio.toFixed(1) })}
          </p>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {/* Not gated on `dirty`. The hex field commits on blur, and that blur is the
            * same interaction that presses Save — so a render where `dirty` is still
            * false can be the render the click lands in, leaving the button inert at
            * exactly the moment it is needed. `save()` reads the live edits itself and
            * no-ops when nothing changed, which is a harmless click; a dead button at
            * the end of typing a colour is not. */}
          <Btn size="sm" disabled={!canWrite || saving} onClick={() => { void save(); }}>{saving ? t("settings.appRegistration.text19", "Saving…") : t("settings.appRegistration.text20", "Save appearance")}</Btn>
        {dirty ? <Btn variant="ghost" size="sm" disabled={saving} onClick={() => { editsRef.current = {}; setEdits({}); }}>{t("settings.appRegistration.text21", "Cancel")}</Btn> : null}
      </div>
    </div>
  );
}
